import _ from "lodash";
/** External */
import React from "react";
import moment from "moment";
import { Trans } from "react-i18next";
/** Design System */
import {
  ScoreBasedAssessmentOutlined,
  CaretUpFilled,
  CaretDownFilled,
  MultipleUsersOutlined,
  HeartLikeOutlined,
  PieOutlined,
  UpArrowOutlined,
  DownArrowOutlined,
  SettingsOutlined,
  AttendanceOutlined,
  StarsTwoFilled,
  PinOutlined,
  GridOutlined,
  AddCircleOutlined,
  CommentSquareLeftClassroomOutlined,
  SinglePointRubricOutlined,
} from "@toddle-edu/ds-icons";
import { Tag, Tooltip } from "@toddle-edu/ds-web";
import { colors } from "@toddle-edu/ds-theme";

/** Internal */
import CustomTag from "ToddleAnalytics/components/Tag";
import {
  getFormattedUserName,
  getUserSortOptions,
} from "Utils/GetFormattedUserName";
import {
  ENGAGEMENT_ANALYTICS,
  DIVIDER,
  MODULE_CONFIGURATION_MAP,
  TODDLE_ANALYTICS_PERMS,
  ALL_OPTION,
  HEADING_LABELS,
  ROUTE_MAP,
  ROUTE_MODULE_MAPPING,
  DEFAULT_SORT_OPTION,
  PERFORMANCE_ANALYTICS,
  DEFAULT_VALUE_FOR_NO_DATA,
  SCORE_RANGES,
  MAX_IB_DP_GRADE,
  DYNAMIC_COLUMNS_INSERT_INDEX_VALUE,
  DYNAMIC_COLUMNS_VALUE_TYPES,
  DYNAMIC_COLUMNS_VALUE_TYPES_VARIABLES,
  COLOR_BUCKETS,
  COLORS_VALUE_RANGE,
  PREVIOUS_ACADEMIC_YEAR,
  CURRENT_ACADEMIC_YEAR,
  CUSTOM_DURATION,
  DATE_RANGE_OPTIONS,
  LEVEL_LOCALES,
  QUERY_PARAM_DELIMITER,
  MONTHLY,
  DAILY,
  MODULE_ACADEMIC_YEAR_PERM_MAP,
  NA,
  PAST_ACADEMIC_YEARS,
  GRADING_PERIOD,
  BEHAVIOUR_ANALYTICS,
  ANALYTICS_ENTITY_TYPE_ENUMS,
  BEHAVIOUR_NOMENCLATURE_CONSTANTS,
  BEHAVIOUR_NOMENCLATURE_LOCALE_PARAM_MAP,
  DASHBOARD_TITLE_MAP,
  CHART_TITLE_MAP,
  FILTER_MAP,
  FILTER_CURRICULUM_PROGRAM,
  FILTER_ORGANIZATION,
  PERFORMANCE_MULTI_TERMS_PERM,
  MULTIGRADING_COLUMN_CONSTANTS,
  NAME_SORT_OPTIONS,
  MAX_CRITERIA_GRADE,
  PERFORMANCE_ANALYTICS_COLOR_PANEL,
  MODULE_USAGE_ANALYTICS,
  DEFAULT_TABLE_COLUMN_WIDTH,
  PLANNING_USAGE,
  MODULE_CUSTOM_DATE_RANGE_SELECTION_PERM_MAP,
  COLUMN_ID_THRESHOLD_PERM_MAPPING,
  ENABLE_PERFORMANCE_THRESHOLDS,
  ATTENDANCE_ANALYTICS,
  ATTENDANCE_RECORDING_TYPE,
  MODULE_PERMS,
  ENABLE_AI_ANALYTICS,
} from "ToddleAnalytics/modules/Constants";
import { getIsCourseBasedGradingEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  getAcademicYearEndDate,
  getAcademicYearStartDate,
  getImmediatePreviousAcademicYear,
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
  getCurrentAcademicYear,
} from "modules/CommonSelectors";
import MoreItemWithToolTip from "UIComponents/MoreItemWithToolTip";
import {
  ENABLE_TODDLE_ANALYTICS,
  ENABLE_ENGAGEMENT_ANALYTICS,
  ENABLE_PERFORMANCE_ANALYTICS,
  ENABLE_BEHAVIOUR_ANALYTICS,
  ENABLE_MODULE_USAGE_ANALYTICS,
  ENABLE_STUDENT_MIDDLE_NAME,
  ENABLE_USER_MIDDLE_NAME,
  TODDLE_ANALYTICS_USER_BASED_ACCESS_PERM,
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  ENABLE_ATTENDANCE_ANALYTICS,
  ENABLE_INSIGHTS_PLAYGROUND,
  ENABLE_ATTENDANCE_TIERS,
  ENABLE_ATTENDANCE_2_0,
  USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
  ENABLE_USAGE_OVERVIEW,
  ENABLE_EXTERNAL_FILE_EDIT,
} from "Constants/permissionConstants";
import {
  ENABLE_ACCOUNT_CONFIGURATION_TERMINOLOGY,
  ENABLE_EXTERNAL_DATA_SOURCE,
  ENABLE_MARKING_RATE_VISIBILITY,
} from "ToddleAnalytics/modules/Constants/ToddleAnalyticsConstants";
import ACLStore from "lib/aclStore";
import {
  isSameDate,
  getDateRange,
  calculateDaysInRange,
  getMomentDateWithSchoolTimeZone,
  getYearDurationFormat,
} from "./DateUtils";
import { readConfigurationFragmentByUID } from "ChildrenPage/modules/ChildrenPageGraphqlHelpers";
import {
  getLabelValueForMultiFilterLabelButton,
  parseConfigurationValue,
} from "Utils";
import {
  getOrganizationGradesFromCache,
  getOrganizationSubjectsFromCache,
  getGradingPeriodsFromCache,
  getAnalyticsEntityFromCache,
  getBehaviourModuleNomenclatureFromCache,
  getLastSyncTimeFromCache,
  getOrganizationLearningCoursesFromCache,
} from "ToddleAnalytics/modules/ToddleAnalyticsGraphqlHelpers";
import {
  generateConfigurationUid,
  getOrganizationDetailsFromCache,
  getOrganizationMultiLevelNodeTagsFromCache,
  getOrganizationGlobalConstantsFromCache,
} from "modules/CommonGraphqlHelpers";
import { getOrganizationCoursesFromCache } from "PerformanceAnalytics/routes/ClassPerformanceAnalytics/modules/ClassPerformanceGraphqlHelpers";
import { getOrganizationStaffFromCache } from "PerformanceAnalytics/routes/StaffPerformanceAnalytics/modules/StaffPerformanceGraphqlHelpers";
import { getOrganizationStudentsFromCache } from "PerformanceAnalytics/routes/StudentPerformanceAnalytics/modules/StudentPerformanceGraphqlHelpers";
import { ATTENDANCE_GLOBAL_CONSTANTS } from "modules/Services";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_PYP,
  ADMIN,
  CURRICULUM_VARIANT_TODDLE_PLAY,
} from "Constants/stringConstants";
import { formatDate } from "Utils/DateTimeHelpers";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { store } from "store/createStore";
import {
  getSettingValue,
  getIsConfigurationEnabledForCPAY,
} from "modules/PermissionModule";
import { getBehaviourCategoryEntityOptions } from "BehaviourAnalytics/modules/BehaviourAnalyticsUtils";
import ACLStorage from "lib/aclService";
import { getJsonItem, setJsonItem } from "services/localStorage";
import { getIsCombineCurriculumHomePageEnabled } from "Platform/modules/PlatformUtils";

const styles = {
  tooltipContainerStyle: {
    maxWidth: "500px",
    maxHeight: "250px",
    overflowY: "auto",
  },
  valueStyle: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

export const isRnpConsumptionEnabled = () =>
  ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION);

export const getModuleConfigurationValue = ({ configuration }) => {
  const isCombinedCurriculum = getIsCombineCurriculumHomePageEnabled();

  if (isCombinedCurriculum) {
    const userInfo = getUserInfo();
    const userCurriculumPrograms = _.get(
      userInfo,
      "userCurriculumPrograms",
      []
    );
    return _.some(userCurriculumPrograms, ({ id }) =>
      ACLStorage.getPermissionValueByContext(configuration, [
        { entityId: id, entityType: "CURRICULUM_PROGRAM" },
      ])
    );
  } else {
    return ACLStorage.getPermissionValue(configuration);
  }
};

const checkIfAnyPermIsEnabled = perms => {
  return _.some(perms, perm => {
    if (ACLStore.can(perm)) return true;
    return getModuleConfigurationValue({ configuration: perm });
  });
};

const analyticsMenuOptions = () => {
  const rootNode = [
    {
      key: "root",
      children: [
        "engagement",
        "module-usage",
        ...(!getIsPerformance2p0Setup() || getIsPerformanceInsights2p0Enabled()
          ? ["performance"]
          : []),
        "attendance",
        "behaviour",
        "ai-analytics-playground",
        "pinned-dashboards-playground",
      ],
      perms: TODDLE_ANALYTICS_PERMS,
    },
  ];

  const engagementAnalyticsNodes = [
    {
      key: "engagement",
      title: "toddleAnalytics:engagement_insights",
      tier: 1,
      parent: "root",
      listType: "accordion",
      isExpanded: true,
      children: [
        "engagement-by-staff",
        "engagement-by-student",
        "engagement-by-family-members",
      ],
      prefix: <MultipleUsersOutlined />,
      perms: [ENABLE_ENGAGEMENT_ANALYTICS],
    },
    {
      key: "engagement-by-staff",
      title: "toddleAnalytics:by_staff",
      parent: "engagement",
      children: [],
      route: "engagement/staff",
      perms: [ENABLE_ENGAGEMENT_ANALYTICS],
    },
    {
      key: "engagement-by-student",
      title: "toddleAnalytics:by_student",
      parent: "engagement",
      children: [],
      route: "engagement/student",
      perms: [ENABLE_ENGAGEMENT_ANALYTICS],
    },
    {
      key: "engagement-by-family-members",
      title: "toddleAnalytics:by_family_members",
      parent: "engagement",
      children: [],
      route: "engagement/family",
      perms: [ENABLE_ENGAGEMENT_ANALYTICS],
    },
  ];

  const performanceAnalyticsNodes = [
    {
      key: "performance",
      title: "toddleAnalytics:performance_insights",
      tier: 1,
      parent: "root",
      listType: "accordion",
      children: [
        "performance-by-grade",
        getBySubjectOrLearningCourseOption(),
        "performance-by-class",
        ...(!getIsPerformanceInsights2p0Enabled()
          ? ["performance-by-staff"]
          : []),
        "performance-by-student",
      ],
      prefix: <ScoreBasedAssessmentOutlined />,
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
    {
      key: "performance-by-grade",
      title: "toddleAnalytics:by_grade",
      parent: "performance",
      children: [],
      route: "performance/grade",
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
    {
      key: "performance-by-subject",
      title: "toddleAnalytics:by_subject",
      parent: "performance",
      children: [],
      route: "performance/subject",
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
    {
      key: "performance-by-learning-course",
      title: "toddleAnalytics:by_course",
      parent: "performance",
      children: [],
      route: "performance/learning-course",
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
    {
      key: "performance-by-class",
      title: "toddleAnalytics:by_class",
      parent: "performance",
      children: [],
      route: "performance/class",
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
    {
      key: "performance-by-staff",
      title: "toddleAnalytics:by_staff",
      parent: "performance",
      children: [],
      route: "performance/staff",
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
    {
      key: "performance-by-student",
      title: "toddleAnalytics:by_student",
      parent: "performance",
      children: [],
      route: "performance/student",
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
  ];

  const behaviourAnalyticsNodes = [
    {
      key: "behaviour",
      title: "toddleAnalytics:behaviour_insights_with_nomenclature",
      tier: 1,
      parent: "root",
      listType: "accordion",
      children: [
        "behaviour-overview",
        "behaviour-by-category",
        "behaviour-by-grade",
        "behaviour-by-student",
      ],
      nomenclatureKey: BEHAVIOUR_NOMENCLATURE_CONSTANTS.MODULE,
      prefix: <HeartLikeOutlined />,
      perms: [ENABLE_BEHAVIOUR_ANALYTICS],
    },
    {
      key: "behaviour-overview",
      title: "common:overview",
      parent: "behaviour",
      children: [],
      route: "behaviour/overview",
      perms: [ENABLE_BEHAVIOUR_ANALYTICS],
    },
    {
      key: "behaviour-by-category",
      title: "toddleAnalytics:by_category",
      parent: "behaviour",
      children: [],
      route: "behaviour/category",
      perms: [ENABLE_BEHAVIOUR_ANALYTICS],
    },
    {
      key: "behaviour-by-grade",
      title: "toddleAnalytics:by_grade",
      parent: "behaviour",
      children: [],
      route: "behaviour/grade",
      perms: [ENABLE_BEHAVIOUR_ANALYTICS],
    },
    {
      key: "behaviour-by-student",
      title: "toddleAnalytics:by_student",
      parent: "behaviour",
      children: [],
      route: "behaviour/student",
      perms: [ENABLE_BEHAVIOUR_ANALYTICS],
    },
  ];

  const moduleUsageAnalyticsNodes = [
    {
      key: "module-usage",
      title: "toddleAnalytics:usage_insights",
      tier: 1,
      parent: "root",
      listType: "accordion",
      children: [
        ...(ACLStore.can(ENABLE_USAGE_OVERVIEW) ? ["overview-usage"] : []),
        "curriculum-planning-usage",
        "assignments-usage",
        "portfolio-usage",
        // "announcements-usage",
        "messaging-usage",
      ],
      prefix: <PieOutlined />,
      perms: [ENABLE_MODULE_USAGE_ANALYTICS],
    },
    {
      key: "overview-usage",
      title: "common:overview",
      parent: "module-usage",
      children: [],
      route: "module-usage/overview",
      perms: [ENABLE_MODULE_USAGE_ANALYTICS],
    },
    {
      key: "curriculum-planning-usage",
      title: "toddleAnalytics:planning",
      parent: "module-usage",
      children: [],
      route: "module-usage/planning",
      perms: [ENABLE_MODULE_USAGE_ANALYTICS],
    },
    {
      key: "assignments-usage",
      title: "common:assignments",
      parent: "module-usage",
      children: [],
      route: "module-usage/assignments",
      perms: [ENABLE_MODULE_USAGE_ANALYTICS],
    },
    {
      key: "portfolio-usage",
      title: "common:portfolio",
      parent: "module-usage",
      children: [],
      route: "module-usage/portfolio",
      perms: [ENABLE_MODULE_USAGE_ANALYTICS],
    },
    {
      key: "announcements-usage",
      title: "common:announcement_plural",
      parent: "module-usage",
      children: [],
      route: "module-usage/announcements",
      perms: [ENABLE_MODULE_USAGE_ANALYTICS],
    },
    {
      key: "messaging-usage",
      title: "common:messaging",
      parent: "module-usage",
      children: [],
      route: "module-usage/messaging",
      perms: [ENABLE_MODULE_USAGE_ANALYTICS],
    },
  ];

  const attendanceAnalyticsNodes = [
    {
      key: "attendance",
      title: "toddleAnalytics:attendance_insights",
      tier: 1,
      parent: "root",
      listType: "accordion",
      children: [
        "attendance-overview",
        "attendance-by-grade",
        "attendance-by-class",
        "attendance-by-student",
      ],
      prefix: <AttendanceOutlined />,
      perms: [ENABLE_ATTENDANCE_ANALYTICS],
    },
    {
      key: "attendance-overview",
      title: "common:overview",
      parent: "attendance",
      children: [],
      route: "attendance/overview",
      perms: [ENABLE_ATTENDANCE_ANALYTICS],
    },
    {
      key: "attendance-by-grade",
      title: "toddleAnalytics:by_grade",
      parent: "attendance",
      children: [],
      route: "attendance/grade",
      perms: [ENABLE_ATTENDANCE_ANALYTICS],
    },
    {
      key: "attendance-by-class",
      title: "toddleAnalytics:by_class",
      parent: "attendance",
      children: [],
      route: "attendance/class",
      perms: [ENABLE_ATTENDANCE_ANALYTICS],
    },
    {
      key: "attendance-by-student",
      title: "toddleAnalytics:by_student",
      parent: "attendance",
      children: [],
      route: "attendance/student",
      perms: [ENABLE_ATTENDANCE_ANALYTICS],
    },
  ];

  const aiInsightsPlaygroundNodes = [
    {
      key: "ai-analytics-playground",
      title: "toddleAnalytics:conversational_insights",
      tier: 1,
      parent: "root",
      children: [],
      route: "ai-playground",
      prefix: <StarsTwoFilled />,
      perms: [ENABLE_INSIGHTS_PLAYGROUND],
    },
    {
      key: "pinned-dashboards-playground",
      title: "Pinned dashboards",
      tier: 1,
      parent: "root",
      children: [],
      route: "pinned-dashboards-playground",
      prefix: <PinOutlined />,
      perms: [ENABLE_INSIGHTS_PLAYGROUND],
    },
  ];

  return [
    ...rootNode,
    ...engagementAnalyticsNodes,
    ...moduleUsageAnalyticsNodes,
    ...performanceAnalyticsNodes,
    ...attendanceAnalyticsNodes,
    ...behaviourAnalyticsNodes,
    ...aiInsightsPlaygroundNodes,
  ];
};

const analyticsMenuOptionsV2 = () => {
  const rootNode = [
    {
      key: "root",
      children: [
        "new-chat",
        "chat-history",
        "external-data-sources",
        "all-dashboards",
        "divider-1",
        "pinned-dashboards",
        "divider-2",
        "engagement",
        "module-usage",
        ...(!getIsPerformance2p0Setup() || getIsPerformanceInsights2p0Enabled()
          ? ["performance"]
          : []),
        "attendance",
        "behaviour",
        "ai-analytics-playground",
      ],
      perms: TODDLE_ANALYTICS_PERMS,
    },
  ];

  const aiInsightsNodes = [
    {
      key: "new-chat",
      title: "common:new_chat",
      parent: "root",
      children: [],
      route: "ai",
      prefix: <AddCircleOutlined />,
      perms: [ENABLE_AI_ANALYTICS],
    },
    {
      key: "chat-history",
      title: "toddleAnalytics:chat_history",
      parent: "root",
      children: [],
      route: "chat-history",
      prefix: <CommentSquareLeftClassroomOutlined />,
      perms: [ENABLE_AI_ANALYTICS],
    },
    {
      key: "external-data-sources",
      title: "toddleAnalytics:external_data_sources",
      parent: "root",
      children: [],
      route: "external-data-sources",
      prefix: <SinglePointRubricOutlined />,
      perms: [ENABLE_EXTERNAL_DATA_SOURCE],
    },
    {
      key: "all-dashboards",
      title: "toddleAnalytics:all_dashboards",
      parent: "root",
      children: [],
      route: "all-dashboards",
      prefix: <GridOutlined />,
      perms: [ENABLE_AI_ANALYTICS],
    },
    {
      key: "divider-1",
      isDivider: true,
      parent: "root",
      perms: [ENABLE_AI_ANALYTICS],
    },
  ];

  const pinnedDashboardsNodes = [
    // When you encounter this node, push new dynamic nodes to children of pinned-dashboards
    {
      key: "pinned-dashboards",
      title: "toddleAnalytics:pinned_dashbaords",
      parent: "root",
      tier: 2,
      children: [],
      perms: [ENABLE_AI_ANALYTICS],
    },
    {
      key: "divider-2",
      isDivider: true,
      parent: "root",
      perms: [ENABLE_AI_ANALYTICS],
    },
  ];

  const toddleDashboardsNodes = [
    {
      key: "toddle-dashboards",
      title: "toddleAnalytics:toddle_dashboards",
      tier: 2,
      parent: "root",
      children: [
        "engagement",
        "module-usage",
        "performance",
        "attendance",
        "behaviour",
      ],
      perms: TODDLE_ANALYTICS_PERMS,
    },
  ];

  const engagementAnalyticsNodes = [
    {
      key: "engagement",
      title: "toddleAnalytics:engagement_insights",
      // tier: 1,
      // parent: "toddle-dashboards",
      parent: "root",
      listType: "accordion",
      isExpanded: true,
      children: [
        "engagement-by-staff",
        "engagement-by-student",
        "engagement-by-family-members",
      ],
      prefix: <MultipleUsersOutlined />,
      perms: [ENABLE_ENGAGEMENT_ANALYTICS],
    },
    {
      key: "engagement-by-staff",
      title: "toddleAnalytics:by_staff",
      parent: "engagement",
      children: [],
      route: "engagement/staff",
      perms: [ENABLE_ENGAGEMENT_ANALYTICS],
    },
    {
      key: "engagement-by-student",
      title: "toddleAnalytics:by_student",
      parent: "engagement",
      children: [],
      route: "engagement/student",
      perms: [ENABLE_ENGAGEMENT_ANALYTICS],
    },
    {
      key: "engagement-by-family-members",
      title: "toddleAnalytics:by_family_members",
      parent: "engagement",
      children: [],
      route: "engagement/family",
      perms: [ENABLE_ENGAGEMENT_ANALYTICS],
    },
  ];

  const performanceAnalyticsNodes = [
    {
      key: "performance",
      title: "toddleAnalytics:performance_insights",
      // tier: 1,
      // parent: "toddle-dashboards",
      parent: "root",
      listType: "accordion",
      children: [
        "performance-by-grade",
        getBySubjectOrLearningCourseOption(),
        "performance-by-class",
        ...(!getIsPerformanceInsights2p0Enabled()
          ? ["performance-by-staff"]
          : []),
        "performance-by-student",
      ],
      prefix: <ScoreBasedAssessmentOutlined />,
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
    {
      key: "performance-by-grade",
      title: "toddleAnalytics:by_grade",
      parent: "performance",
      children: [],
      route: "performance/grade",
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
    {
      key: "performance-by-subject",
      title: "toddleAnalytics:by_subject",
      parent: "performance",
      children: [],
      route: "performance/subject",
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
    {
      key: "performance-by-learning-course",
      title: "toddleAnalytics:by_course",
      parent: "performance",
      children: [],
      route: "performance/learning-course",
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
    {
      key: "performance-by-class",
      title: "toddleAnalytics:by_class",
      parent: "performance",
      children: [],
      route: "performance/class",
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
    {
      key: "performance-by-staff",
      title: "toddleAnalytics:by_staff",
      parent: "performance",
      children: [],
      route: "performance/staff",
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
    {
      key: "performance-by-student",
      title: "toddleAnalytics:by_student",
      parent: "performance",
      children: [],
      route: "performance/student",
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
  ];

  const behaviourAnalyticsNodes = [
    {
      key: "behaviour",
      title: "toddleAnalytics:behaviour_insights_with_nomenclature",
      // tier: 1,
      // parent: "toddle-dashboards",
      parent: "root",
      listType: "accordion",
      children: [
        "behaviour-overview",
        "behaviour-by-category",
        "behaviour-by-grade",
        "behaviour-by-student",
      ],
      nomenclatureKey: BEHAVIOUR_NOMENCLATURE_CONSTANTS.MODULE,
      prefix: <HeartLikeOutlined />,
      perms: [ENABLE_BEHAVIOUR_ANALYTICS],
    },
    {
      key: "behaviour-overview",
      title: "common:overview",
      parent: "behaviour",
      children: [],
      route: "behaviour/overview",
      perms: [ENABLE_BEHAVIOUR_ANALYTICS],
    },
    {
      key: "behaviour-by-category",
      title: "toddleAnalytics:by_category",
      parent: "behaviour",
      children: [],
      route: "behaviour/category",
      perms: [ENABLE_BEHAVIOUR_ANALYTICS],
    },
    {
      key: "behaviour-by-grade",
      title: "toddleAnalytics:by_grade",
      parent: "behaviour",
      children: [],
      route: "behaviour/grade",
      perms: [ENABLE_BEHAVIOUR_ANALYTICS],
    },
    {
      key: "behaviour-by-student",
      title: "toddleAnalytics:by_student",
      parent: "behaviour",
      children: [],
      route: "behaviour/student",
      perms: [ENABLE_BEHAVIOUR_ANALYTICS],
    },
  ];

  const moduleUsageAnalyticsNodes = [
    {
      key: "module-usage",
      title: "toddleAnalytics:usage_insights",
      // tier: 1,
      // parent: "toddle-dashboards",
      parent: "root",
      listType: "accordion",
      children: [
        ...(ACLStore.can(ENABLE_USAGE_OVERVIEW) ? ["overview-usage"] : []),
        "curriculum-planning-usage",
        "assignments-usage",
        "portfolio-usage",
        "messaging-usage",
      ],
      prefix: <PieOutlined />,
      perms: [ENABLE_MODULE_USAGE_ANALYTICS],
    },
    {
      key: "overview-usage",
      title: "common:overview",
      parent: "module-usage",
      children: [],
      route: "module-usage/overview",
      perms: [ENABLE_MODULE_USAGE_ANALYTICS],
    },
    {
      key: "curriculum-planning-usage",
      title: "toddleAnalytics:planning",
      parent: "module-usage",
      children: [],
      route: "module-usage/planning",
      perms: [ENABLE_MODULE_USAGE_ANALYTICS],
    },
    {
      key: "assignments-usage",
      title: "common:assignments",
      parent: "module-usage",
      children: [],
      route: "module-usage/assignments",
      perms: [ENABLE_MODULE_USAGE_ANALYTICS],
    },
    {
      key: "portfolio-usage",
      title: "common:portfolio",
      parent: "module-usage",
      children: [],
      route: "module-usage/portfolio",
      perms: [ENABLE_MODULE_USAGE_ANALYTICS],
    },
    {
      key: "announcements-usage",
      title: "common:announcement_plural",
      parent: "module-usage",
      children: [],
      route: "module-usage/announcements",
      perms: [ENABLE_MODULE_USAGE_ANALYTICS],
    },
    {
      key: "messaging-usage",
      title: "common:messaging",
      parent: "module-usage",
      children: [],
      route: "module-usage/messaging",
      perms: [ENABLE_MODULE_USAGE_ANALYTICS],
    },
  ];

  const attendanceAnalyticsNodes = [
    {
      key: "attendance",
      title: "toddleAnalytics:attendance_insights",
      // tier: 1,
      // parent: "toddle-dashboards",
      parent: "root",
      listType: "accordion",
      children: [
        "attendance-overview",
        "attendance-by-grade",
        "attendance-by-class",
        "attendance-by-student",
      ],
      prefix: <AttendanceOutlined />,
      perms: [ENABLE_ATTENDANCE_ANALYTICS],
    },
    {
      key: "attendance-overview",
      title: "common:overview",
      parent: "attendance",
      children: [],
      route: "attendance/overview",
      perms: [ENABLE_ATTENDANCE_ANALYTICS],
    },
    {
      key: "attendance-by-grade",
      title: "toddleAnalytics:by_grade",
      parent: "attendance",
      children: [],
      route: "attendance/grade",
      perms: [ENABLE_ATTENDANCE_ANALYTICS],
    },
    {
      key: "attendance-by-class",
      title: "toddleAnalytics:by_class",
      parent: "attendance",
      children: [],
      route: "attendance/class",
      perms: [ENABLE_ATTENDANCE_ANALYTICS],
    },
    {
      key: "attendance-by-student",
      title: "toddleAnalytics:by_student",
      parent: "attendance",
      children: [],
      route: "attendance/student",
      perms: [ENABLE_ATTENDANCE_ANALYTICS],
    },
  ];

  const aiInsightsPlaygroundNodes = [
    {
      key: "ai-analytics-playground",
      title: "Insights playground",
      tier: 1,
      parent: "root",
      children: [],
      route: "ai-playground",
      prefix: <StarsTwoFilled />,
      perms: [ENABLE_INSIGHTS_PLAYGROUND],
    },
  ];

  return [
    ...rootNode,
    ...aiInsightsNodes,
    ...pinnedDashboardsNodes,
    ...toddleDashboardsNodes,
    ...engagementAnalyticsNodes,
    ...moduleUsageAnalyticsNodes,
    ...performanceAnalyticsNodes,
    ...attendanceAnalyticsNodes,
    ...behaviourAnalyticsNodes,
    ...aiInsightsPlaygroundNodes,
  ];
};

const analyticsFooterOptions = () => {
  const rootNode = [
    {
      key: "footerRoot",
      children: ["settings"],
      modulePerm: ENABLE_PERFORMANCE_THRESHOLDS,
      perms: [ENABLE_PERFORMANCE_ANALYTICS, ENABLE_AI_ANALYTICS], // Settings support both performance thresholds and email digest
    },
  ];

  const settingsNodes = [
    {
      key: "settings",
      title: "common:settings",
      tier: 1,
      parent: "footerRoot",
      isSliderSubmenu: true,
      children: ["performance-thresholds", "email-digest"],
      prefix: <SettingsOutlined />,
      perms: [ENABLE_PERFORMANCE_ANALYTICS, ENABLE_AI_ANALYTICS], // To-do: Add RnP module permission here
    },
    {
      key: "performance-thresholds",
      title: "toddleAnalytics:performance_thresholds",
      parent: "settings",
      children: [],
      route: "settings/performance-thresholds",
      perms: [ENABLE_PERFORMANCE_ANALYTICS],
    },
    {
      key: "email-digest",
      title: "common:email_digest",
      parent: "settings",
      children: [],
      route: "settings/email-digest",
      perms: [ENABLE_AI_ANALYTICS],
    },
  ];

  return [...rootNode, ...settingsNodes];
};

export const isAIAnalyticsEnabled = () => ACLStore.can(ENABLE_AI_ANALYTICS);

export const getAnalyticsMenuOptions = ({
  t,
  location,
  pinnedDashboards,
  handlePinClick,
  renderPinButton,
}) => {
  const analyticsNodes = isAIAnalyticsEnabled()
    ? analyticsMenuOptionsV2()
    : analyticsMenuOptions();
  const options = _.reduce(
    analyticsNodes,
    (menuOptions, option) => {
      const { key, perms, listType, nomenclatureKey, parent } = option;

      if (!_.isEqual(key, "root")) {
        const parentNode = _.find(menuOptions, { key: parent });
        const parentChildren = _.get(parentNode, "children", []);

        // Remove current node if it's key not in children of it's parent node
        if (!_.includes(parentChildren, key)) return menuOptions;
      }

      if (!_.isEmpty(perms) && checkIfAnyPermIsEnabled(perms)) {
        if (_.isEqual(listType, "accordion")) {
          option.isExpanded = _.includes(location.pathname, key);
        }

        let title;
        // Apply nomenclature for behaviour module
        if (nomenclatureKey && _.isEqual(key, "behaviour")) {
          const localeParams = getNomenclatureLocaleParams({
            type: nomenclatureKey,
            convertToLowerCase: false,
          });
          title = t(option.title, localeParams);
        } else {
          title = t(option.title);
        }

        const children = _.filter(option.children, childOption => {
          const childOptionDetails = _.find(analyticsNodes, {
            key: childOption,
          });

          // Remove module if it's module permission is not enabled
          if (
            MODULE_PERMS[childOption] &&
            !ACLStore.can(MODULE_PERMS[childOption])
          ) {
            return false;
          }

          // Remove performance analytics node if the only curriculums that exist are PYP, PLAY and a UBD curriculum with overallScore disabled
          if (_.isEqual(childOption, "performance")) {
            const performanceAnalyticsCurriculumOptions = getPerformanceAnalyticsCurriculumOptions();

            if (_.isEmpty(performanceAnalyticsCurriculumOptions)) return false;
          }

          // Remove class attendance analytics node if there is no curriculum with period recording type
          if (_.isEqual(childOption, "attendance-by-class")) {
            const curriculumOptions = getClassAttendanceAnalyticsCurriculumOptions();
            if (_.isEmpty(curriculumOptions)) return false;
          }

          const state = store?.getState();
          const isToddleSupportOrDevUser = _.get(
            state,
            "login.userInfo.isDevUser",
            false
          );

          // Remove attendance analytics node of 'FeatureFlag:EnableAttendaceInsightsForToddleSupportOnly' is enabled and user is not toddle support
          if (_.isEqual(childOption, "attendance")) {
            const enabledForToddleSupportOnly = ACLStore.can(
              "FeatureFlag:EnableAttendaceInsightsForToddleSupportOnly"
            );

            if (enabledForToddleSupportOnly && !isToddleSupportOrDevUser) {
              return false;
            }
          }

          // Remove AI analytics playground node if it's not a toddle support user
          if (
            _.isEqual(childOption, "ai-analytics-playground") ||
            _.isEqual(childOption, "pinned-dashboards-playground")
          ) {
            if (!isToddleSupportOrDevUser) return false;
          }

          if (_.includes(["pinned-dashboards", "divider-2"], childOption)) {
            // If the child option is pinned-dashboards, check if there are any pinned dashboards
            if (_.isEmpty(pinnedDashboards)) return false;
          }

          return checkIfAnyPermIsEnabled(childOptionDetails.perms);
        });
        menuOptions.push({ ...option, title, children });
      }

      // If there are no pinned-dashboards, we don't show the pinned-dashboards node, so node should be removed too
      if (_.isEqual(key, "divider-2") && _.isEmpty(pinnedDashboards))
        return menuOptions;

      // If the node is pinned-dashboards, we need to dynamically add the pinned dashboards to its children
      if (_.isEqual(key, "pinned-dashboards")) {
        // If there are no pinned dashboards, we don't add the node
        if (_.isEmpty(pinnedDashboards)) return menuOptions;

        const queryVariables = getInsightDashboardQueryVariables();

        let pinnedDashboardChildren = [];
        if (pinnedDashboards.length > 6) {
          // Show first 6, then "View more"
          pinnedDashboardChildren = _.map(
            pinnedDashboards.slice(0, 6),
            dashboard => {
              const { id, name } = dashboard;
              return {
                key: `pinned-dashboard-${id}`,
                title: name,
                parent: "pinned-dashboards",
                tier: 1,
                children: [],
                suffix: renderPinButton(id, handlePinClick, queryVariables),
                route: `pinned-dashboards/${id}`,
                perms: [ENABLE_AI_ANALYTICS],
              };
            }
          );
          // // Add "View all" as the fourth option
          // pinnedDashboardChildren.push({
          //   key: "viewAllPinnedDashboards",
          //   title: t("common:view_all"),
          //   parent: "pinned-dashboards",
          //   tier: 1,
          //   children: [],
          //   route: "pinned-dashboards/view-all",
          //   perms: [ENABLE_AI_ANALYTICS],
          //   isViewMore: true,
          // });
        } else {
          pinnedDashboardChildren = _.map(pinnedDashboards, dashboard => {
            const { id, name } = dashboard;
            return {
              key: `pinned-dashboard-${id}`,
              title: name,
              parent: "pinned-dashboards",
              tier: 1,
              children: [],
              suffix: renderPinButton(id, handlePinClick, queryVariables),
              route: `pinned-dashboards/${id}`,
              perms: [ENABLE_AI_ANALYTICS],
            };
          });
        }
        const pinnedDashboardsNode = _.find(menuOptions, {
          key: "pinned-dashboards",
        });
        pinnedDashboardsNode.children = _.map(pinnedDashboardChildren, "key");
        menuOptions.push(...pinnedDashboardChildren);
      }

      return menuOptions;
    },
    []
  );

  return options;
};

export const getAnalyticsPinnedDashboardMenuOptions = ({
  pinnedDashboards,
  handlePinClick,
  renderPinButton,
  queryVariables,
}) => {
  // Extract dashboard nodes from edges if present
  let dashboardNodes = [];
  if (_.isArray(pinnedDashboards) && pinnedDashboards.length > 0) {
    // If edges structure, extract node
    if (_.has(pinnedDashboards[0], "node")) {
      dashboardNodes = _.map(pinnedDashboards, edge => edge.node).filter(
        Boolean
      );
    } else {
      dashboardNodes = pinnedDashboards;
    }
  }

  // Each dashboard will be a direct child of root
  const dashboardKeys = dashboardNodes.map(d => `pinned-dashboard-${d.id}`);

  const rootNode = [
    {
      key: "root",
      children: dashboardKeys,
    },
  ];

  const dashboardMenuOptions = dashboardNodes.map(d => ({
    key: `pinned-dashboard-${d.id}`,
    title: d.name || "Untitled",
    parent: "root",
    tier: 1,
    children: [],
    suffix: renderPinButton(d.id, handlePinClick, queryVariables),
    route: `pinned-dashboards/${d.id}`,
    perms: [ENABLE_AI_ANALYTICS],
  }));

  // If no dashboards, return root with no children
  if (_.isEmpty(dashboardNodes)) {
    return [
      {
        key: "root",
        children: [],
      },
    ];
  }

  return rootNode.concat(dashboardMenuOptions);
};

export const getAnalyticsMenuFooterOptions = ({ t }) => {
  const footerNodes = analyticsFooterOptions();
  const options = _.reduce(
    footerNodes,
    (footerOptions, option) => {
      const { key, perms, modulePerm, parent } = option;

      if (!_.isEqual(key, "footerRoot")) {
        const rootNode = _.find(footerOptions, { key: "footerRoot" });
        const rootChildren = _.get(rootNode, "children", []);

        // Remove current node if it's key or it's parent is not in children of root node
        if (!_.includes(rootChildren, key) && !_.includes(rootChildren, parent))
          return footerOptions;
      }

      if (modulePerm && !ACLStore.can(modulePerm)) {
        return footerOptions;
      }

      if (!_.isEmpty(perms) && checkIfAnyPermIsEnabled(perms)) {
        const title = t(option.title);
        const children = _.filter(option.children, childOption => {
          const childOptionDetails = _.find(footerNodes, {
            key: childOption,
          });
          return checkIfAnyPermIsEnabled(childOptionDetails.perms);
        });
        footerOptions.push({ ...option, title, children });
      }
      return footerOptions;
    },
    []
  );

  return options;
};

const getLocalisedText = ({ t, value }) => {
  if (React.isValidElement(value)) return value;
  return _.isString(value) ? t(value) : t(value.key, value.params);
};

export const avatarColorMap = ["violet", "blue", "yellow", "pink", "teal"];

export const avatarBgColors = [
  colors.violet900,
  colors.blue900,
  colors.yellow900,
  colors.pink900,
  colors.teal900,
];

export const avatarTextColors = [
  colors.violet300,
  colors.blue300,
  colors.yellow300,
  colors.pink300,
  colors.teal300,
];

const getConfigUidBySrc = ({ src, configuration, ...entityData }) => {
  const { curriculumProgramId, userId } = entityData;

  const ENTITY_GROUPS = {
    CURRICULUM_LEVEL: [
      { entityId: curriculumProgramId, entityType: FILTER_CURRICULUM_PROGRAM },
      { entityId: userId, entityType: "USER" },
    ],
  };

  return generateConfigurationUid({
    entityGroups: ENTITY_GROUPS[src],
    configName: configuration,
  });
};

export const getStaffCurriculumQueryVariables = ({
  userId,
  selectedAcademicYearId,
}) => ({
  ids: [userId],
  academicYearId: selectedAcademicYearId,
});

export const sortSubjects = ({ subjects, orderByDirection }) => {
  return _.orderBy(
    subjects,
    ["isArchived", subject => _.toLower(_.get(subject, "name", ""))],
    ["asc", _.lowerCase(orderByDirection)]
  );
};

export const getCurriculumProgramDetails = curriculumId => {
  const userInfo = getUserInfo();
  const userCurriculumPrograms = _.get(userInfo, "userCurriculumPrograms", []);
  const selectedProgram = _.find(userCurriculumPrograms, { id: curriculumId });

  const type = _.get(selectedProgram, "type", "");
  const acronym = _.get(selectedProgram, "acronym", "");

  const isPYP = _.isEqual(type, CURRICULUM_TYPE_PYP, false);
  const isDP = _.isEqual(type, CURRICULUM_TYPE_DP, false);
  const isMYP = _.isEqual(type, CURRICULUM_TYPE_MYP, false);
  const isUBD = _.isEqual(type, CURRICULUM_TYPE_UBD, false);

  return {
    id: curriculumId,
    type,
    acronym,
    isPYP,
    isDP,
    isMYP,
    isUBD,
  };
};

const filterProgramsByType = (programs, programTypes) => {
  if (_.isEmpty(programTypes)) return programs;
  return _.filter(programs, program =>
    _.includes(programTypes, _.get(program, "type"))
  );
};

const removeVariants = (programs, UBDVariantsToHide, programTypes) => {
  if (
    _.isEmpty(UBDVariantsToHide) ||
    !_.includes(programTypes, CURRICULUM_TYPE_UBD)
  )
    return programs;

  return _.filter(programs, program => {
    if (_.isEqual(_.get(program, "type"), CURRICULUM_TYPE_UBD)) {
      const variant = _.get(program, "variant");
      return !_.includes(UBDVariantsToHide, variant);
    }
    return true;
  });
};

const filterEnabledPrograms = (programs, perm, userId) => {
  return _.filter(programs, program => {
    if (!perm) return true;

    // Old configuration logic
    // To-do: Remove this once attendance configuration is migrated to RnP
    const uid = getConfigUidBySrc({
      src: "CURRICULUM_LEVEL",
      configuration: perm,
      curriculumProgramId: program.id,
      userId,
    });
    const configurationData = readConfigurationFragmentByUID({ uid });

    // Determine if the feature is enabled for this curriculum program
    const context = [
      { entityType: "CURRICULUM_PROGRAM", entityId: program.id },
    ];
    const isPermissionEnabledViaRnP = ACLStorage.getPermissionValueByContext(
      perm,
      context
    );

    const isToddleAnalyticsEnabledViaRnP = ACLStorage.getModuleSettingsValueByContext(
      ENABLE_TODDLE_ANALYTICS,
      context
    );

    const isEnabledViaRnP =
      isPermissionEnabledViaRnP && isToddleAnalyticsEnabledViaRnP;

    // Check legacy configuration system
    const isEnabledViaLegacyConfig =
      !_.isNil(configurationData) && parseConfigurationValue(configurationData);

    const isEnabledOnCurriculum = isEnabledViaRnP || isEnabledViaLegacyConfig;

    // Show UBD curriculum in performance analytics only if performance analytics and overallScore are enabled
    if (
      _.isEqual(program.type, CURRICULUM_TYPE_UBD) &&
      _.isEqual(perm, MODULE_CONFIGURATION_MAP[PERFORMANCE_ANALYTICS])
    ) {
      const isScoreEnabled = isOverallScoreEnabled({
        curriculumProgramId: program.id,
      });
      return isEnabledOnCurriculum && isScoreEnabled;
    }

    return isEnabledOnCurriculum;
  });
};

const mapToCurriculumOptions = programs => {
  return _.map(programs, ({ id, label, type }) => ({
    key: id,
    value: id,
    label,
    curriculumType: type,
  }));
};

export const getPerformanceAnalyticsCurriculumOptions = () => {
  const userInfo = getUserInfo();
  const userCurriculumPrograms = userInfo.userCurriculumPrograms;

  return getCurriculumOptions({
    userCurriculumPrograms,
    perm: MODULE_CONFIGURATION_MAP[PERFORMANCE_ANALYTICS],
    programTypes: [
      CURRICULUM_TYPE_MYP,
      CURRICULUM_TYPE_DP,
      CURRICULUM_TYPE_UBD,
    ],
    UBDVariantsToHide: [CURRICULUM_VARIANT_TODDLE_PLAY],
  });
};

export const getCurriculumOptions = ({
  userCurriculumPrograms = [],
  perm,
  programTypes,
  UBDVariantsToHide = [],
}) => {
  const { id: userId } = getUserInfo();

  // Filter programs by type if programTypes is not empty
  const filteredProgramsByType = filterProgramsByType(
    userCurriculumPrograms,
    programTypes
  );

  // Filter out UBD variants specified in UBDVariantsToHide
  const filteredPrograms = removeVariants(
    filteredProgramsByType,
    UBDVariantsToHide,
    programTypes
  );

  // Filter programs based on the permissions
  const enabledCurriculumPrograms = filterEnabledPrograms(
    filteredPrograms,
    perm,
    userId
  );

  return mapToCurriculumOptions(enabledCurriculumPrograms);
};

export const shouldShowAcademicYearSwitcher = moduleName => {
  if (!moduleName) return false;
  const perm = MODULE_ACADEMIC_YEAR_PERM_MAP[moduleName];
  if (!perm) return false;
  return ACLStore.can(perm);
};

export const shouldAllowCustomDateRangeSelection = moduleName => {
  if (!moduleName) return true;
  const perm = MODULE_CUSTOM_DATE_RANGE_SELECTION_PERM_MAP[moduleName];
  if (!perm) return true;
  return ACLStore.can(perm, "one", true);
};

export const shouldShowPerformanceAcademicYearSwitcher = () => {
  return shouldShowAcademicYearSwitcher(PERFORMANCE_ANALYTICS);
};

const getGradingPeriods = ({ curriculumId, academicYearId }) => {
  const userInfo = getUserInfo();
  const userId = userInfo.id;
  const academicYears = _.get(
    getGradingPeriodsFromCache({ id: userId }),
    "academicYears",
    []
  );
  const selectedAcademicYear = _.find(academicYears, { id: academicYearId });

  const gradingPeriodOptions = _.reduce(
    _.get(selectedAcademicYear, "terms", []),
    (options, { id, label, curriculumProgram, type, startDate, endDate }) => {
      if (!curriculumId || curriculumProgram?.id === curriculumId) {
        options.push({ key: id, value: id, label, type, startDate, endDate });
      }
      return options;
    },
    []
  );

  return gradingPeriodOptions;
};

export const getGradingPeriodsMemoized = _.memoize(
  params => getGradingPeriods(params),
  ({ curriculumId, academicYearId }) =>
    JSON.stringify({ curriculumId, academicYearId })
);

const getDateDropdownGradingPeriodOption = ({
  curriculumProgramId,
  key,
  label,
  t,
}) => {
  const userInfo = getUserInfo();
  const organizationId = userInfo.org_id;

  let gradingPeriods = getGradingPeriodsMemoized({
    curriculumId: curriculumProgramId,
    academicYearId: _.get(getCurrentAcademicYear({ organizationId }), "id"),
  });

  gradingPeriods = _.filter(gradingPeriods, ({ startDate }) => {
    const currentDate = getMomentDateWithSchoolTimeZone().startOf("day");
    const termStartDate = getMomentDateWithSchoolTimeZone(
      startDate,
      true
    ).startOf("day");
    return termStartDate.isBefore(currentDate);
  });

  if (!_.isEmpty(gradingPeriods)) {
    return {
      key,
      label: t(label),
      isSubMenu: true,
      options: _.map(gradingPeriods, ({ key, label }) => ({
        key,
        label,
      })),
    };
  }

  return null;
};

const getDateDropdownAcademicYearOption = ({ key, label, t }) => {
  const pastAcademicYears = getLastNAcademicYearOptions({
    n: 2,
    includeCurrentAY: false,
  });

  if (!_.isEmpty(pastAcademicYears)) {
    return {
      key,
      label: t(label),
      isSubMenu: true,
      options: pastAcademicYears,
    };
  }

  return null;
};

const getCustomDurationSubText = ({
  dateViewType,
  rangeStartDate,
  rangeEndDate,
  dateFormat,
}) => {
  if (_.isEqual(dateViewType, CUSTOM_DURATION)) {
    const startDateText = formatDate({
      date: rangeStartDate,
      format: dateFormat,
    });
    const endDateText = formatDate({ date: rangeEndDate, format: dateFormat });
    return `${startDateText} - ${endDateText}`;
  }

  return null;
};

export const getDateDropdownOptions = ({
  dateViewType,
  rangeStartDate,
  rangeEndDate,
  moduleName = ENGAGEMENT_ANALYTICS,
  curriculumProgramId,
  t,
}) => {
  const dateFormat = DATE_TIME_CONSTANTS.D_MMM_YYYY;
  const isAcademicYearSwitcherEnabled = shouldShowAcademicYearSwitcher(
    moduleName
  );
  const isCustomDateRangeSelectionEnabled = shouldAllowCustomDateRangeSelection(
    moduleName
  );

  return _.reduce(
    DATE_RANGE_OPTIONS,
    (options, { key, label, modules }) => {
      // If the module is not present in the modules array, then skip the option
      if (!_.includes(modules, moduleName)) return options;

      // If previous academic year is not enabled, then skip the option
      if (
        _.includes([PREVIOUS_ACADEMIC_YEAR, PAST_ACADEMIC_YEARS], key) &&
        !isAcademicYearSwitcherEnabled
      ) {
        return options;
      }

      // If custom date range selection is not enabled, then skip the option
      if (
        _.isEqual(key, CUSTOM_DURATION) &&
        !isCustomDateRangeSelectionEnabled
      ) {
        return options;
      }

      if (_.isEqual(key, GRADING_PERIOD) && curriculumProgramId) {
        const gradingPeriodOptions = getDateDropdownGradingPeriodOption({
          curriculumProgramId,
          key,
          label,
          t,
        });
        if (gradingPeriodOptions) options.push(gradingPeriodOptions);
        return options;
      }

      if (_.isEqual(key, PAST_ACADEMIC_YEARS)) {
        const pastAcademicYearOptions = getDateDropdownAcademicYearOption({
          key,
          label,
          t,
        });
        if (pastAcademicYearOptions) options.push(pastAcademicYearOptions);
        return options;
      }

      const option = {
        key,
        label: t(label),
      };

      if (
        _.isEqual(key, CUSTOM_DURATION) &&
        _.isEqual(dateViewType, CUSTOM_DURATION)
      ) {
        option.subText = getCustomDurationSubText({
          dateViewType,
          rangeStartDate,
          rangeEndDate,
          dateFormat,
        });
      }

      options.push(option);

      return options;
    },
    []
  );
};

export const addDividerOption = options => {
  return [{ key: DIVIDER, isDivider: true }, ...options];
};

export const getSortByDropdownOptions = ({ t }) => {
  return [
    {
      key: "low_to_high",
      label: t("common:low_to_high"),
    },
    {
      key: "high_to_low",
      label: t("common:high_to_low"),
    },
  ];
};

export const getPerformanceBarChartTabOptions = ({ t }) => {
  return [
    { value: "IB_GRADE", label: t("common:IB_grade") },
    { value: "SCORE", label: t("common:score") },
  ];
};

export const getScrollbarWidth = () => {
  const dummyElement = document.createElement("div");
  dummyElement.style.width = "100px";
  dummyElement.style.height = "100px";
  dummyElement.style.overflow = "scroll";
  dummyElement.style.visibility = "hidden";

  document.body.appendChild(dummyElement);

  // offsetWidth: total width including content, padding, borders, and the scrollbar (if present).
  // clientWidth: width including content area, excluding padding, borders, and the scrollbar.
  const scrollbarWidth = dummyElement.offsetWidth - dummyElement.clientWidth;

  document.body.removeChild(dummyElement);

  return scrollbarWidth;
};

export const getTrendComponent = ({
  trend,
  iconSize = "xxx-small",
  isRevampEnabled = false,
}) => {
  if (_.isNull(trend) || _.isUndefined(trend)) return null;

  const renderIcon = (Component, style) => (
    <Component variant={style.variant} size={iconSize} style={style.style} />
  );

  const iconMap = {
    "+": () =>
      renderIcon(isRevampEnabled ? UpArrowOutlined : CaretUpFilled, {
        variant: "success",
        style: isRevampEnabled ? { color: "var(--tag-foreground-teal)" } : null,
      }),
    "-": () =>
      renderIcon(isRevampEnabled ? DownArrowOutlined : CaretDownFilled, {
        variant: "critical",
        style: isRevampEnabled ? { color: "var(--text-semantic-error)" } : null,
      }),
  };

  return iconMap[trend]?.() || null;
};

export const appendPercent = ({ variable }) => {
  if (_.isNull(variable) || _.isUndefined(variable) || _.isNaN(variable))
    return "";
  return variable + "%";
};

export const trimNumber = (num = 0, decimalCount = 2) => {
  num = _.toNumber(num);
  if (_.isNaN(num)) return NaN;
  return parseFloat(num.toFixed(decimalCount));
};

export const getParentBackgroundColor = currentElement => {
  if (!currentElement || !currentElement.parentElement) return null;

  const computedStyle = window.getComputedStyle(currentElement);
  const backgroundColor = computedStyle.backgroundColor;

  const transparentColor = "rgba(0, 0, 0, 0)";
  if (backgroundColor !== transparentColor) return backgroundColor;

  return getParentBackgroundColor(currentElement.parentElement);
};

export const getLegendOptions = lineOptions => {
  const result = _.map(
    lineOptions,
    ({ legendLabel, tooltipLabel, stroke, displaySequence }) => ({
      label: legendLabel ?? tooltipLabel,
      color: stroke,
      displaySequence,
    })
  );

  return _.orderBy(result, "displaySequence", "asc");
};

export const getMoreItemsWithToolTipComponent = ({
  items,
  customStyles = {},
  length = 1,
  truncateLimit = 28,
}) => {
  const lengthOfItems = _.size(items);
  if (_.isEqual(lengthOfItems, 0)) return "-";
  const moreItemsComponent = (
    <MoreItemWithToolTip
      items={items}
      tooltipPlacement={"right"}
      length={length}
      moreAll={true}
      trigger={"hover"}
      truncateLimit={truncateLimit}
      joinSymbol={", "}
      tooltipContainerStyle={styles.tooltipContainerStyle}
      closeOnScroll={true}
      stopPropagationOnLabelClick={false}
      {...customStyles}
    />
  );
  return _.isEqual(lengthOfItems, 1) ? (
    <Tooltip tooltip={items[0]} placement={"top"}>
      <div
        style={
          customStyles.valueStyle ? customStyles.valueStyle : styles.valueStyle
        }
      >
        {items[0]}
      </div>
    </Tooltip>
  ) : (
    moreItemsComponent
  );
};

// Function to set an item in local storage with an expiry time (in seconds)
export function setItemWithExpiry(key, value, expiryInSeconds = 3600) {
  const now = new Date();
  const expiryTime = now.getTime() + expiryInSeconds * 1000; // Convert seconds to milliseconds

  const item = {
    value: value,
    expiry: expiryTime,
  };
  setJsonItem({ key, value: item });
}

// Function to get an item from local storage, considering the expiry time
export function getItemWithExpiry(key) {
  const item = getJsonItem(key);
  if (!item) {
    return null;
  }

  const now = new Date();

  // Check if the current time is past the expiry time
  // or if the difference is more than 1 hour (3600 seconds)
  const timeDifference = Math.abs(now.getTime() - item.expiry);
  if (now.getTime() > item.expiry || timeDifference > 3600 * 1000) {
    localStorage.removeItem(key);
    return null;
  }

  return item.value;
}

const saveIntoStorage = (key, value) => setItemWithExpiry(key, value);

const getFromStorage = key => getItemWithExpiry(key);

export const isFirstVisitToday = () => {
  const analyticsStorageData = getFromStorage("analytics") || {};
  const lastVisit = _.get(analyticsStorageData, "lastVisit", null);
  if (!lastVisit) {
    return true;
  }
  const savedUserIdForAnalytics = _.get(analyticsStorageData, "userId", "");
  const userInfo = getUserInfo({ portalType: "PLANNER" });
  const userId = userInfo.id;
  return (
    !_.isEqual(userId, savedUserIdForAnalytics) ||
    !isSameDate(
      getMomentDateWithSchoolTimeZone(lastVisit),
      getMomentDateWithSchoolTimeZone(),
      { compareTime: false }
    )
  );
};

export const setUserVisitDataIntoStorage = ({ userId, timeStamp }) => {
  const analyticsStorageData = getFromStorage("analytics") || {};
  analyticsStorageData.lastVisit = timeStamp
    ? timeStamp.format()
    : getMomentDateWithSchoolTimeZone();
  analyticsStorageData.userId = userId;
  saveIntoStorage("analytics", analyticsStorageData);
};

export const renderTags = ({ tags, customTag = false, size = "x-small" }) => {
  const TagComponent = customTag ? CustomTag : Tag;
  if (_.isArray(tags) && _.size(tags)) {
    return (
      <div style={{ display: "flex", flexDirection: "row", gap: "6px" }}>
        {_.map(tags, tag => (
          <TagComponent color={tag == "HL" ? "pink" : "teal"} size={size}>
            {tag}
          </TagComponent>
        ))}
      </div>
    );
  }
  return null;
};

export const getArrowStyle = (position, arrowSize = 10) => {
  const positionShift = 1.5; // to expose enough of the background
  const border = `${arrowSize}px solid ${colors.surfaceWhite}`;

  switch (position) {
    case "left":
      return {
        right: positionShift,
        borderLeft: border,
      };
    case "right":
      return {
        left: positionShift,
        borderRight: border,
      };
    case "bottom":
      return {
        top: positionShift,
        borderBottom: border,
      };
    default:
      return {
        bottom: positionShift,
        borderTop: border,
      };
  }
};

export const getArrayOfColumnIdsFromMap = columnIdsMap => {
  return _.filter(_.keys(columnIdsMap), key => columnIdsMap[key]);
};

export const getMapFromArrayOfColumnIds = columnIdsArray => {
  return _.reduce(
    columnIdsArray,
    (columnIdsMap, columnId) => {
      columnIdsMap[columnId] = true;
      return columnIdsMap;
    },
    {}
  );
};

export const getFormattedTableData = ({
  FIELDUID_COLUMNID_MAPPING,
  tableData,
}) => {
  const formatTableRow = tableRow =>
    _.reduce(
      tableRow,
      (resultantRow, { field, stringField, value, metaData }) => {
        const val = _.size(value) > 1 ? value : _.get(value, "0", "");
        const doesStringFieldContainGradingPeriodId =
          !_.isEmpty(stringField) && _.includes(stringField, "__");
        //all gradingPeriodId contained stringFields will have __

        if (!_.isEmpty(metaData)) {
          const { categoryId, valueType } = metaData;

          const splittedStringField = _.split(stringField, "__");
          const gradingPeriodId = _.get(splittedStringField, "1");

          const columnId = doesStringFieldContainGradingPeriodId
            ? `${categoryId}_${valueType}_${gradingPeriodId}`
            : `${categoryId}_${valueType}`;
          resultantRow[columnId] = val;
        } else {
          const splittedStringField = _.split(stringField, "__");
          const gradingPeriodId = _.get(splittedStringField, "1");

          const columnId = doesStringFieldContainGradingPeriodId
            ? `${FIELDUID_COLUMNID_MAPPING[field]}_${gradingPeriodId}`
            : FIELDUID_COLUMNID_MAPPING[field];
          resultantRow[columnId] = val;
        }
        return resultantRow;
      },
      {}
    );

  return _.map(tableData, tableRow => formatTableRow(tableRow));
};

export const getMonthlyGraphData = graphData => {
  const monthlyData = _.map(graphData, data => {
    const month = _.get(data, "xAxis", "");
    const activeUser = _.toNumber(_.get(data.yAxis, "[0].value", 0));
    const fullDate = moment(month, "YYYY-MM").format("MMMM YYYY");
    const dateAbbr = moment(month, "YYYY-MM").format("MMM, YYYY");

    return {
      dateAbbr,
      fullDate,
      activeUser,
    };
  });

  const sortedMonthlyData = _.sortBy(monthlyData, item =>
    moment(item.fullDate, "MMMM YYYY")
  );

  return sortedMonthlyData;
};

export const getDailyGraphData = graphData => {
  return _.map(graphData, data => {
    const { xAxis, yAxis } = data;
    const fullDate = moment(xAxis).format("ddd MMM DD, YYYY");
    const dateAbbr = moment(xAxis).format("DD MMM, YYYY");
    const activeUser = _.toNumber(_.get(yAxis, "0.value", 0));

    return {
      dateAbbr,
      fullDate,
      activeUser,
    };
  });
};

export const getUserEngagementFormattedGraphData = (
  graphData,
  timeSeriesInterval
) => {
  if (_.isEqual(timeSeriesInterval, MONTHLY))
    return getMonthlyGraphData(graphData);

  return getDailyGraphData(graphData);
};

export const getUserEngagementAreaOptions = ({ legendLabel, tooltipLabel }) => {
  return [
    {
      dataKey: "activeUser",
      type: "linear",
      stroke: colors.interactiveThreeDefault,
      fill: colors.interactiveThreeDefault,
      strokeWidth: 2,
      dot: false,
      legendLabel,
      tooltipLabel,
    },
  ];
};

export const getSubmoduleDetails = () => {
  const path = location.pathname;
  const isStaffPerformanceModule = _.includes(path, "performance/staff");
  const isClassDetailedView = _.includes(path, "/class/");
  const isStaffDetailedView = _.includes(path, "/staff/");
  return { isStaffPerformanceModule, isClassDetailedView, isStaffDetailedView };
};

export const getModuleNameDetails = () => {
  const moduleName = getModuleName(location.pathname);
  const isEngagementAnalytics = _.isEqual(moduleName, ENGAGEMENT_ANALYTICS);
  const isPerformanceAnalytics = _.isEqual(moduleName, PERFORMANCE_ANALYTICS);
  const isModuleUsageAnalytics = _.isEqual(moduleName, MODULE_USAGE_ANALYTICS);
  const isBehaviourAnalytics = _.isEqual(moduleName, BEHAVIOUR_ANALYTICS);
  const isAttendanceAnalytics = _.isEqual(moduleName, ATTENDANCE_ANALYTICS);

  return {
    isEngagementAnalytics,
    isPerformanceAnalytics,
    isModuleUsageAnalytics,
    isBehaviourAnalytics,
    isAttendanceAnalytics,
  };
};

export const getCurriculumProgramsIdsForQuery = ({
  curriculumProgramIds,
  moduleName = ENGAGEMENT_ANALYTICS,
}) => {
  // If no curriculum is selected, then get all the curriculum programs
  if (_.isEmpty(curriculumProgramIds)) {
    const userInfo = getUserInfo();
    const curriculumOptions = getCurriculumOptions({
      userCurriculumPrograms: userInfo.userCurriculumPrograms,
      perm: MODULE_CONFIGURATION_MAP[moduleName],
    });
    curriculumProgramIds = _.map(curriculumOptions, "value");
  }
  return curriculumProgramIds;
};

export const getSubjectIdsForQuery = ({
  subjectIds,
  curriculumId,
  gradeId,
}) => {
  const { selectedAcademicYearId } = extractIdsFromUrlSearchParams();

  if (_.isArray(subjectIds)) {
    const subjectOptions = getSubjectOptionsMemoized({
      curriculumId,
      gradeId,
      selectedAcademicYearId,
      removeSubjectsWithoutClasses: false,
    });
    if (_.isEqual(_.size(subjectIds), _.size(subjectOptions))) return [];
  }
  return subjectIds;
};

export const getUserEngagementGraphDataQueryVariables = ({
  orgId,
  curriculumProgramIds,
  dateRange,
  userType,
}) => {
  const { rangeStart, rangeEnd } = getDateRange(dateRange);
  const numberOfDaysInRange = calculateDaysInRange({
    startDate: rangeStart,
    endDate: rangeEnd,
  });

  const dateRangeVariables = {
    rangeStart,
    rangeEnd,
    timeSeriesInterval: numberOfDaysInRange >= 90 ? MONTHLY : DAILY,
  };

  return {
    id: orgId,
    graphDataFilters: {
      moduleType: ENGAGEMENT_ANALYTICS,
      xAxisKey: "TIME_SERIES",
      yAxisKey: "ACTIVE_COUNT",
      engagementGraphFilter: {
        curriculumProgramIds: getCurriculumProgramsIdsForQuery({
          curriculumProgramIds,
        }),
        userType,
        ...dateRangeVariables,
      },
    },
  };
};

export const getUserEngagementOverviewDataQueryVariables = ({
  orgId,
  curriculumProgramIds,
  dateRange,
  userType,
}) => {
  return {
    id: orgId,
    overViewMetricFilters: {
      moduleType: ENGAGEMENT_ANALYTICS,
      engagementOverviewMetricsFilter: {
        curriculumProgramIds: getCurriculumProgramsIdsForQuery({
          curriculumProgramIds,
        }),
        userType,
        ...getDateRange(dateRange),
      },
    },
  };
};

export const getOverviewCardTrendValue = ({
  overviewMetricsFieldValueMap,
  activeFieldValue,
  pastFieldValue,
  isPercentageValue,
  showOnlyValueDifference = false,
}) => {
  const pastValue = _.toNumber(
    _.get(overviewMetricsFieldValueMap, pastFieldValue, 0)
  );
  const currentValue = _.toNumber(
    _.get(overviewMetricsFieldValueMap, activeFieldValue, 0)
  );

  const valueDifference = trimNumber(Math.abs(currentValue - pastValue));
  const percentageDifference =
    pastValue === 0 ? 0 : trimNumber((valueDifference / pastValue) * 100);

  const showPercent = showOnlyValueDifference && isPercentageValue;

  return percentageDifference === 0 || showOnlyValueDifference
    ? `${valueDifference}${showPercent ? "%" : ""}`
    : `${valueDifference} (${percentageDifference}%)`;
};

export const getOverviewCardTrendSymbol = ({
  overviewMetrics,
  overviewMetricsFieldValueMap,
  activeFieldValue,
  pastFieldValue,
}) => {
  overviewMetricsFieldValueMap =
    overviewMetricsFieldValueMap ||
    getOverviewMetricsFieldValueMap(overviewMetrics);

  const pastValue = _.toNumber(
    _.get(overviewMetricsFieldValueMap, pastFieldValue, 0)
  );
  const currentValue = _.toNumber(
    _.get(overviewMetricsFieldValueMap, activeFieldValue, 0)
  );

  return pastValue === currentValue
    ? "="
    : pastValue > currentValue
    ? "-"
    : "+";
};

export const getOverviewMetricsFieldValueMap = (
  overviewMetrics,
  fieldKey = "field",
  getPastValueFromMetadata = false
) => {
  return _.reduce(
    overviewMetrics,
    (result, metric) => {
      const field = metric[fieldKey]; // Dynamically get the field value
      if (field) {
        result[field] = metric.value?.[0]; // Safely access value[0]

        if (getPastValueFromMetadata) {
          result[`${field}_PAST`] = metric.metaData?.past_value;
        }
      }
      return result;
    },
    {}
  );
};

export const isAcadmicYearFilter = dateViewType => {
  return _.includes(
    [CURRENT_ACADEMIC_YEAR, PREVIOUS_ACADEMIC_YEAR],
    dateViewType
  );
};

export const getStaffOverviewMetricsFormattedData = ({
  t,
  dateViewType,
  overviewMetrics,
}) => {
  const overviewMetricsFieldValueMap = getOverviewMetricsFieldValueMap(
    overviewMetrics
  );

  const trendParams = {
    overviewMetricsFieldValueMap,
    activeFieldValue: "ACTIVE_STAFF",
    pastFieldValue: "PAST_ACTIVE_STAFF",
  };

  return [
    {
      value:
        overviewMetricsFieldValueMap["TOTAL_STAFF"] ||
        DEFAULT_VALUE_FOR_NO_DATA,
      subtitle: t("toddleAnalytics:staff_members_with_label", {
        label: t("common:total"),
      }),
    },
    {
      value:
        overviewMetricsFieldValueMap["ACTIVE_STAFF"] ||
        DEFAULT_VALUE_FOR_NO_DATA,
      subtitle: t("toddleAnalytics:staff_members_with_label", {
        label: t("common:active"),
      }),
      helpIconTooltipProps: {
        tooltipHeading: t("toddleAnalytics:active_staff_members"),
        tooltipText: t(
          "toddleAnalytics:user_engagement_help_icon_text_with_label",
          {
            label: t("toddleAnalytics:staff_members_lowercase"),
          }
        ),
      },
      trendProps: !isAcadmicYearFilter(dateViewType)
        ? {
            trend: getOverviewCardTrendSymbol(trendParams),
            trendValue: getOverviewCardTrendValue(trendParams),
          }
        : null,
    },
  ];
};

export const getStudentOverviewMetricsFormattedData = ({
  t,
  dateViewType,
  overviewMetrics,
}) => {
  const overviewMetricsFieldValueMap = getOverviewMetricsFieldValueMap(
    overviewMetrics
  );

  const trendParams = {
    overviewMetricsFieldValueMap,
    activeFieldValue: "ACTIVE_STUDENT",
    pastFieldValue: "PAST_ACTIVE_STUDENT",
  };

  return [
    {
      value:
        overviewMetricsFieldValueMap["TOTAL_STUDENT"] ||
        DEFAULT_VALUE_FOR_NO_DATA,
      subtitle: t("toddleAnalytics:total_with_label", {
        label: t("common:student_plural_lowercase"),
      }),
    },
    {
      value:
        overviewMetricsFieldValueMap["ACTIVE_STUDENT"] ||
        DEFAULT_VALUE_FOR_NO_DATA,
      subtitle: t("toddleAnalytics:active_with_label", {
        label: t("common:student_plural_lowercase"),
      }),
      helpIconTooltipProps: {
        tooltipHeading: t("toddleAnalytics:active_with_label", {
          label: t("common:student_plural_lowercase"),
        }),
        tooltipText: t(
          "toddleAnalytics:user_engagement_help_icon_text_with_label",
          {
            label: t("common:student_plural_lowercase"),
          }
        ),
      },
      trendProps: !isAcadmicYearFilter(dateViewType)
        ? {
            trend: getOverviewCardTrendSymbol(trendParams),
            trendValue: getOverviewCardTrendValue(trendParams),
          }
        : null,
    },
  ];
};

export const getFamilyOverviewMetricsFormattedData = ({
  t,
  dateViewType,
  overviewMetrics,
}) => {
  const overviewMetricsFieldValueMap = getOverviewMetricsFieldValueMap(
    overviewMetrics
  );

  const trendParams = {
    overviewMetricsFieldValueMap,
    activeFieldValue: "ACTIVE_FAMILY_MEMBER",
    pastFieldValue: "PAST_ACTIVE_FAMILY_MEMBER",
  };

  return [
    {
      value:
        overviewMetricsFieldValueMap["TOTAL_FAMILY_MEMBER"] ||
        DEFAULT_VALUE_FOR_NO_DATA,
      subtitle: t("toddleAnalytics:total_with_label", {
        label: t("toddleAnalytics:family_members_lowercase"),
      }),
    },
    {
      value:
        overviewMetricsFieldValueMap["CONNECTED_FAMILY_MEMBER"] ||
        DEFAULT_VALUE_FOR_NO_DATA,
      subtitle: t("toddleAnalytics:family_members_with_label", {
        label: t("common:connected"),
      }),
      helpIconTooltipProps: {
        tooltipHeading: t("toddleAnalytics:family_members_with_label", {
          label: t("common:connected"),
        }),
        tooltipText: t(
          "toddleAnalytics:the_metric_is_the_count_of_number_of_family_members"
        ),
      },
    },
    {
      value:
        overviewMetricsFieldValueMap["ACTIVE_FAMILY_MEMBER"] ||
        DEFAULT_VALUE_FOR_NO_DATA,
      subtitle: t("toddleAnalytics:active_with_label", {
        label: t("toddleAnalytics:family_members_lowercase"),
      }),
      helpIconTooltipProps: {
        tooltipHeading: t("toddleAnalytics:active_with_label", {
          label: t("toddleAnalytics:family_members"),
        }),
        tooltipText: t(
          "toddleAnalytics:user_engagement_help_icon_text_with_label",
          {
            label: t("toddleAnalytics:family_members_lowercase"),
          }
        ),
      },
      trendProps: !isAcadmicYearFilter(dateViewType)
        ? {
            trend: getOverviewCardTrendSymbol(trendParams),
            trendValue: getOverviewCardTrendValue(trendParams),
          }
        : null,
    },
  ];
};

export const getConfigureColumnsList = ({
  t,
  tableColumnsList,
  curriculumProgramId,
  selectedChartViewByOption,
  appendSubText,
}) => {
  const configurationColumnIds = {};
  /*we have some multiple columns linked to same configuration
   so we will check if we had already added into the list before adding */
  return _.reduce(
    tableColumnsList,
    (columnsList, column) => {
      const {
        subIds = [],
        label,
        group,
        curriculumPrograms,
        isDefaultColumn,
        hideInConfigurationPopup,
        isParent = false,
        isDynamicColumn = false,
        perm,
        readPermFromCurriculumLevel,
        permFn,
      } = column;

      if (permFn && !permFn()) {
        return columnsList;
      }

      if (perm) {
        let isEnabled = true;
        if (readPermFromCurriculumLevel) {
          isEnabled = getSettingValue({
            name: perm,
            currentCurriculumProgramId: curriculumProgramId,
          });
        }

        if (!isEnabled) return columnsList;

        if (!ACLStore.can(perm)) {
          return columnsList;
        }
      }

      const id = isDynamicColumn
        ? _.get(column, "configurationId")
        : _.get(column, "id");
      const configurationLabel = isDynamicColumn
        ? _.get(column, "configurationLabel")
        : label;
      const subText = _.get(column, "subText", null);

      const { type: curriculumProgramType } = !_.isEmpty(curriculumProgramId)
        ? getCurriculumProgramDetails(curriculumProgramId)
        : {};

      const isCurriculumDependent = !_.isUndefined(curriculumPrograms);
      const isValidForCurrentCurriculum = isCurriculumDependent
        ? _.includes(curriculumPrograms, curriculumProgramType)
        : true; //default value will be true for those columns which are not curriculum dependent
      const isConfigurationAddedToList = !configurationColumnIds[id];

      const chartViewByOptions = _.get(column, "chartViewByOptions", null);

      if (
        !_.isEqual(chartViewByOptions, null) &&
        !_.includes(chartViewByOptions, selectedChartViewByOption)
      )
        return columnsList;

      if (
        !isParent &&
        !hideInConfigurationPopup &&
        isValidForCurrentCurriculum &&
        isConfigurationAddedToList
      ) {
        const label =
          appendSubText && !_.isEqual(subText, null)
            ? getLocalisedText({ t, value: configurationLabel }) +
              ` (${t(subText)})`
            : getLocalisedText({ t, value: configurationLabel });
        columnsList.push({
          id,
          subIds,
          label,
          group: _.isEmpty(group) ? "" : t(group),
          isDefaultColumn,
        });
        configurationColumnIds[id] = true;
      }
      return columnsList;
    },
    []
  );
};

export const getSortedFieldsWithOrderObject = sortOptions => {
  const { orderBy: menuItemEntity, orderByDirection: direction } = sortOptions;
  return {
    [menuItemEntity]: { direction, menuItemEntity },
  };
};

export const getEmptyRows = (count = 15) => {
  return new Array(count).fill([]);
};

export const getLoadingStates = ({
  isLoading,
  isDataInCache,
  networkStatus,
  areTableFiltersDefault,
}) => {
  const showFullSkeletonLoader =
    isLoading && !isDataInCache && areTableFiltersDefault; //Initial loading
  const showInnerSkeletonLoader =
    isLoading && !isDataInCache && !areTableFiltersDefault; // loader to show when sort or search input is given
  const showPaginationLoader = _.isEqual(networkStatus, 3);
  const showErrorState = !isLoading && !isDataInCache;

  return {
    showErrorState,
    showPaginationLoader,
    showFullSkeletonLoader,
    showInnerSkeletonLoader,
  };
};

export const appendEmptyRows = ({ data, count }) => [
  ...data,
  ...getEmptyRows(count),
];

export const isGraphqlQueryLoading = networkStatus => {
  return _.includes(GRAPHQL_ONGOING_QUERY_NETWORK_STATUS, networkStatus);
};

export const getImmediatePreviousAcademicYearDetails = () => {
  const userInfo = getUserInfo();
  const organizationId = userInfo.org_id;
  const organizationDetails =
    getOrganizationDetailsFromCache(organizationId) || {};

  const allAcademicYears = _.get(organizationDetails, "academicYears", []);
  const currentAcademicYear = getCurrentAcademicYear({ organizationId });

  return getImmediatePreviousAcademicYear({
    academicYears: allAcademicYears,
    selectedAcademicYear: currentAcademicYear,
  });
};

export const getCurrentAcademicYearStartDate = () => {
  const userInfo = getUserInfo();
  const orgId = userInfo.org_id;
  return getAcademicYearStartDate({ organizationId: orgId });
};

export const getCurrentAcademicYearEndDate = () => {
  const userInfo = getUserInfo();
  const orgId = userInfo.org_id;
  return getAcademicYearEndDate({ organizationId: orgId });
};

export const getDateRangeForCurrentAYOption = () => {
  const currentAYStartDate = getCurrentAcademicYearStartDate();
  const currentAYEndDate = getCurrentAcademicYearEndDate();
  const currentDate = getMomentDateWithSchoolTimeZone();

  const finalEndDate = currentDate.isBefore(currentAYEndDate)
    ? currentDate.subtract(1, "day")
    : currentAYEndDate;
  return {
    startDate: getMomentDateWithSchoolTimeZone(
      currentAYStartDate,
      true
    ).startOf("day"),
    endDate: getMomentDateWithSchoolTimeZone(finalEndDate, true).endOf("day"),
  };
};

export const getDateRangeForPreviousAYOption = () => {
  const previousAY = getImmediatePreviousAcademicYearDetails();
  const previousAYStartDate = _.get(previousAY, "startDate", "");
  const previousAYEndDate = _.get(previousAY, "endDate", "");

  return {
    startDate: getMomentDateWithSchoolTimeZone(
      previousAYStartDate,
      true
    ).startOf("day"),
    endDate: getMomentDateWithSchoolTimeZone(previousAYEndDate, true).endOf(
      "day"
    ),
  };
};

export const getAllAcademicYears = () => {
  const userInfo = getUserInfo();
  const organizationId = userInfo.org_id;
  const organizationDetails =
    getOrganizationDetailsFromCache(organizationId) || {};

  return _.get(organizationDetails, "academicYears", []);
};

export const getDateRangeForAcademicYear = ({ academicYearId }) => {
  const allAcademicYears = getAllAcademicYears();

  const academicYear = _.find(allAcademicYears, { id: academicYearId });
  const startDate = _.get(academicYear, "startDate");
  const endDate = _.get(academicYear, "endDate");

  return {
    startDate: getMomentDateWithSchoolTimeZone(startDate, true).startOf("day"),
    endDate: getMomentDateWithSchoolTimeZone(endDate, true).endOf("day"),
  };
};

export const getDateRangeOfGradingPeriod = id => {
  const {
    selectedCurriculumId,
    selectedAcademicYearId,
  } = extractIdsFromUrlSearchParams();
  const gradingPeriodOptions = getGradingPeriodsMemoized({
    curriculumId: selectedCurriculumId,
    academicYearId: selectedAcademicYearId,
  });
  const gradingPeriod = _.find(gradingPeriodOptions, { value: id });
  const startDate = _.get(gradingPeriod, "startDate");
  const endDate = _.get(gradingPeriod, "endDate");

  return {
    startDate: getMomentDateWithSchoolTimeZone(startDate, true).startOf("day"),
    endDate: getMomentDateWithSchoolTimeZone(endDate, true).endOf("day"),
  };
};

export const getDateRangeOfAcademicYear = id => {
  const academicYears = getUserAcademicYears();
  const academicYear = _.find(academicYears, { value: id });
  const startDate = _.get(academicYear, "startDate");
  const endDate = _.get(academicYear, "endDate");

  return {
    startDate: getMomentDateWithSchoolTimeZone(startDate, true).startOf("day"),
    endDate: getMomentDateWithSchoolTimeZone(endDate, true).endOf("day"),
  };
};

export const getFieldUids = ({
  FIELDUID_COLUMNID_MAPPING,
  configuredColumns,
}) => {
  const configuredUIDS = _.filter(_.keys(FIELDUID_COLUMNID_MAPPING), key => {
    const isColumnConfigured =
      configuredColumns[FIELDUID_COLUMNID_MAPPING[key]];
    return isColumnConfigured;
  });

  configuredUIDS.push("PROFILE_URL");

  return configuredUIDS;
};

const getSortedProgrammesOrder = ({
  programmesToSort,
  userCurriculumPrograms,
}) => {
  let curriculumPrograms = userCurriculumPrograms;
  if (!curriculumPrograms) {
    const userInfo = getUserInfo();
    curriculumPrograms = _.get(userInfo, "userCurriculumPrograms", []);
  }
  const filteredCurriculums = _.filter(curriculumPrograms, program => {
    const acronym = _.get(program, "acronym");
    return _.includes(programmesToSort, acronym);
  });

  return _.map(filteredCurriculums, program => _.get(program, "acronym"));
};

export const getSortedProgrammesOrderMemoized = _.memoize(
  params => getSortedProgrammesOrder(params),
  ({ programmesToSort, userCurriculumPrograms }) =>
    JSON.stringify({
      programmesToSort,
      userCurriculumPrograms: _.map(userCurriculumPrograms, program => ({
        acronym: _.get(program, "acronym"),
      })),
    })
);

export const getLabelValueForMultiSelectFilter = ({
  options,
  selectedOptions,
  ...rest
}) => {
  if (selectedOptions.length === 1) {
    const selectedOption = _.find(options, { value: selectedOptions[0] });
    return selectedOption?.label;
  }
  return getLabelValueForMultiFilterLabelButton({
    options,
    selectedOptions,
    ...rest,
  });
};

export const getGradeOptions = ({
  curriculumId,
  applyCurriculumFilterOnQuery = false,
}) => {
  const userInfo = getUserInfo();
  const orgId = userInfo.org_id;
  const variables = { id: orgId };
  if (applyCurriculumFilterOnQuery && curriculumId) {
    variables["gradeFilters"] = { curriculumProgramIds: [curriculumId] };
  }
  const grades = _.get(getOrganizationGradesFromCache(variables), "grades", []);

  const options = _.reduce(
    grades,
    (filteredOptions, grade) => {
      const { id, name, curriculumProgram } = grade;
      // Filter grades based on curriculumId, if provided
      if (!curriculumId || _.isEqual(curriculumId, curriculumProgram?.id)) {
        const option = {
          key: id,
          value: id,
          label: name,
        };
        filteredOptions.push(option);
      }
      return filteredOptions;
    },
    []
  );

  return options;
};

export const getStaffOptions = ({
  curriculumId,
  gradeIds,
  selectedAcademicYearId,
}) => {
  const userInfo = getUserInfo();
  const orgId = userInfo.org_id;
  const variables = {
    id: orgId,
    staffFilters: {
      curriculumProgramIds: curriculumId ? [curriculumId] : [],
      academicYearIds: _.compact([selectedAcademicYearId]),
      grades: gradeIds,
    },
    allGradesFilters: {
      curriculumProgramIds: _.compact([curriculumId]),
    },
  };
  const staff = _.get(
    getOrganizationStaffFromCache(variables),
    "staff.edges",
    []
  );
  const options = _.map(
    staff,
    ({
      node: {
        firstName = "",
        middleName = "",
        lastName = "",
        id,
        profileImage,
        allGrades = [],
      },
    }) => {
      const staffObject = {
        firstName,
        middleName,
        lastName,
      };

      const staffName = getFormattedUserName({
        user: staffObject,
        type: "staff",
      });

      return {
        label: staffName,
        value: id,
        key: id,
        profileImage,
        gradeOptions: _.map(allGrades, ({ id, name }) => ({
          key: id,
          value: id,
          label: name,
        })),
      };
    }
  );

  return options;
};

export const getCourseOptions = ({
  curriculumId,
  gradeIds,
  subjectIds,
  selectedAcademicYearId,
}) => {
  const userInfo = getUserInfo();
  const orgId = userInfo.org_id;
  const variables = {
    id: orgId,
    courseFilters: {
      curriculumProgramIds: curriculumId ? [curriculumId] : [],
      grades: gradeIds,
      academicYears: _.compact([selectedAcademicYearId]),
    },
    courseGradeFilters: { academicYearId: selectedAcademicYearId },
  };
  const classes = _.get(
    getOrganizationCoursesFromCache(variables),
    "courses.edges",
    []
  );

  const { isDP } = getCurriculumProgramDetails(curriculumId);
  const isRevampEnabled = checkIsRevampPermEnabled();

  const options = _.reduce(
    classes,
    (filteredOptions, course) => {
      const {
        node: {
          title,
          id,
          genericTags = [],
          subjects = [],
          grades = [],
          primaryGrade,
          learningCourse,
        },
      } = course;

      // Filter courses based on subjectId, if provided
      const isSubjectInSelectedCourse =
        _.isEmpty(subjectIds) ||
        _.some(subjectIds, id => _.find(subjects, { id }));

      const { levelIds, levelLabels } = _.reduce(
        genericTags,
        (result, tag = {}) => {
          const type = _.get(tag, "type", "");
          if (!_.isEqual(type, "SUBJECT_LEVEL")) return result;
          result.levelIds.push(_.get(tag, "id", ""));
          result.levelLabels.push(_.get(tag, "label", ""));
          return result;
        },
        { levelIds: [], levelLabels: [] }
      );

      const isGenericClass = !isDP || !_.isEmpty(levelLabels);

      if (isSubjectInSelectedCourse && isGenericClass) {
        const option = {
          key: id,
          value: id,
          label: title,
          levelLabels,
          levelIds,
          ...(!getIsCourseBasedGradingEnabled()
            ? {
                subjectOptions: _.map(subjects, ({ id, name }) => ({
                  key: id,
                  value: id,
                  label: name,
                })),
              }
            : { learningCourse }),
          gradeOptions: _.map(grades, ({ id, name }) => ({
            key: id,
            value: id,
            label: name,
          })),
          primaryGrade,
          suffix: renderTags({
            tags: levelLabels,
            size: isRevampEnabled ? "small" : "x-small",
          }),
        };
        filteredOptions.push(option);
      }

      return filteredOptions;
    },
    []
  );

  return options;
};

export const getLearningCourseOptions = ({
  curriculumId,
  gradeId,
  selectedAcademicYearId,
}) => {
  const userInfo = getUserInfo();
  const orgId = userInfo.org_id;

  const variables = getOrganizationLearningCoursesQueryVariables({
    orgId,
    curriculumId,
    academicYearId: selectedAcademicYearId,
  });

  const learningCourses = _.get(
    getOrganizationLearningCoursesFromCache(variables),
    "learningCourses.edges",
    []
  );

  const options = _.reduce(
    learningCourses,
    (filteredOptions, { node }) => {
      const grades = _.get(node, "grades", []);

      const isInSelectedGrade =
        !gradeId || _.some(grades, grade => _.isEqual(grade.id, gradeId));

      if (!isInSelectedGrade) {
        return filteredOptions;
      }

      const option = {
        key: node.id,
        value: node.id,
        label: node.title,
      };

      filteredOptions.push(option);

      return filteredOptions;
    },
    []
  );

  return options;
};

export const getLearningCourseOptionsMemoized = _.memoize(
  params => getLearningCourseOptions(params),
  ({ curriculumId, gradeId, selectedAcademicYearId }) =>
    JSON.stringify({ curriculumId, gradeId, selectedAcademicYearId })
);

export const getStudentOptions = ({
  curriculumId,
  gradeIds,
  selectedAcademicYearId,
}) => {
  const userInfo = getUserInfo();
  const orgId = userInfo.org_id;
  const variables = {
    id: orgId,
    studentFilters: {
      curriculumProgramIds: _.compact([curriculumId]),
      academicYearIds: _.compact([selectedAcademicYearId]),
      grades: gradeIds,
    },
  };
  const students = _.get(
    getOrganizationStudentsFromCache(variables),
    "students.edges",
    []
  );
  const options = _.reduce(
    students,
    (filteredOptions, student) => {
      const {
        node: {
          firstName = "",
          middleName = "",
          lastName = "",
          id,
          profileImage,
        },
      } = student;

      const studentObject = {
        firstName,
        middleName,
        lastName,
      };

      const studentName = getFormattedUserName({
        user: studentObject,
        type: "student",
      });
      const option = {
        label: studentName,
        value: id,
        key: id,
        profileImage,
      };
      filteredOptions.push(option);

      return filteredOptions;
    },
    []
  );

  return options;
};

export const getSubjectOptions = ({
  curriculumId,
  gradeId,
  applyCurriculumFilterOnQuery = false,
  separateByVariants = false,
  applyGenericFilter = true,
  removeSubjectsWithoutClasses = false,
  selectedAcademicYearId,
}) => {
  const userInfo = getUserInfo();
  const orgId = userInfo.org_id;
  const variables = {
    id: orgId,
    subjectFilters: { archivalState: "ALL" },
    subjectCourseFilters: { academicYearId: selectedAcademicYearId },
    flatVariantsCourseFilters: { academicYearId: selectedAcademicYearId },
  };
  if (applyCurriculumFilterOnQuery && curriculumId) {
    variables["subjectFilters"]["curriculumProgramIds"] = [curriculumId];
  }
  if (applyGenericFilter) {
    variables["subjectFilters"]["types"] = ["GENERIC"];
  }

  const subjects = sortSubjects({
    subjects: _.get(
      getOrganizationSubjectsFromCache(variables),
      "subjects",
      []
    ),
    orderByDirection: "ASC",
  });

  const isRevampEnabled = checkIsRevampPermEnabled();

  const options = _.reduce(
    subjects,
    (filteredOptions, subject) => {
      const { id, name, grades, courses } = subject;

      // Filter subjects based on curriculumId, if provided
      const isSubjectInSelectedCurriculum =
        !curriculumId ||
        _.some(grades, grade =>
          _.isEqual(grade?.curriculumProgram?.id, curriculumId)
        );

      // Filter subjects based on gradeId, if provided
      const isSubjectInSelectedGrade =
        !gradeId || _.find(grades, { id: gradeId });

      // Filter subjects based on courses, if removeSubjectsWithoutClasses is true
      const isSubjectWithClasses = ({ courses }) => {
        return (
          !removeSubjectsWithoutClasses ||
          _.some(courses, ({ node }) => {
            const grades = _.get(node, "grades", []);
            return _.some(grades, grade => _.isEqual(grade.id, gradeId));
          })
        );
      };

      if (
        isSubjectInSelectedCurriculum &&
        isSubjectInSelectedGrade &&
        isSubjectWithClasses({ courses: _.get(courses, "edges") })
      ) {
        const variants = _.get(subject, "flatVariants", []);
        if (!separateByVariants || _.isEmpty(variants)) {
          filteredOptions.push({ key: id, value: id, label: name });
        } else {
          _.forEach(variants, variant => {
            const { level, courses } = variant;

            // Filter subject variant based on courses, if removeSubjectsWithoutClasses is true
            const isSubjectVariantWithClasses = isSubjectWithClasses({
              courses: _.get(courses, "edges"),
            });

            if (isSubjectVariantWithClasses) {
              const levelId = _.get(level, "id", "");
              const levelLabel = _.get(level, "label", "");
              const optionId = levelId ? `${id}_${levelId}` : id;

              const option = {
                key: optionId,
                value: id,
                label: name,
                levelLabels: [levelLabel],
                suffix: renderTags({
                  tags: [levelLabel],
                  size: isRevampEnabled ? "small" : "x-small",
                }),
              };
              filteredOptions.push(option);
            }
          });
        }
      }

      return filteredOptions;
    },
    []
  );

  return _.uniqBy(options, "key");
};

export const getSubjectOptionsMemoized = _.memoize(
  params => getSubjectOptions(params),
  ({
    curriculumId,
    gradeId,
    applyCurriculumFilterOnQuery = false,
    separateByVariants = false,
    applyGenericFilter = true,
    removeSubjectsWithoutClasses = false,
    selectedAcademicYearId,
  }) =>
    JSON.stringify({
      curriculumId,
      gradeId,
      applyCurriculumFilterOnQuery,
      separateByVariants,
      applyGenericFilter,
      removeSubjectsWithoutClasses,
      selectedAcademicYearId,
    })
);

export const getSubjectLevelOptions = ({ showAllOption = false }) => {
  const userInfo = getUserInfo();

  const isPerformanceInsights2p0Enabled = getIsPerformanceInsights2p0Enabled();
  const { selectedCurriculumId } = extractIdsFromUrlSearchParams();

  const { isDP } = selectedCurriculumId
    ? getCurriculumProgramDetails(selectedCurriculumId)
    : { isDP: false };

  const subjectLevelQueryVariables = {
    organizationId: userInfo.org_id,
    filters: {
      types: ["SUBJECT_LEVEL"],
      ...(selectedCurriculumId && isPerformanceInsights2p0Enabled && isDP
        ? { curriculumProgramIds: [selectedCurriculumId] }
        : {}),
    },
  };
  const subjectLevelData = getOrganizationMultiLevelNodeTagsFromCache(
    subjectLevelQueryVariables
  );
  const subjectLevels = _.get(subjectLevelData, "genericTagsV2", []);
  const options = _.map(subjectLevels, ({ id, label }) => ({
    key: id,
    value: id,
    label,
  }));

  return showAllOption ? [ALL_OPTION, ...options] : options;
};

export const getLevelLabel = ({ levelId }) => {
  const levelOptions = getSubjectLevelOptions({});
  const levelObj = _.find(levelOptions, level =>
    _.isEqual(levelId, _.get(level, "key"))
  );
  return _.get(levelObj, "label");
};

export const getLevelLabels = levels => {
  return _.isArray(levels)
    ? _.map(levels, item => getLevelLabel({ levelId: item }))
    : getLevelLabel({ levelId: levels });
};

const getGradingPeriodColumnProps = ({
  t,
  id,
  gradingPeriodId,
  fieldUID,
  column,
  styles,
}) => {
  const isDynamicColumn = _.get(column, "isDynamicColumn", false);

  const label = _.get(column, "label");
  const newLabel = isDynamicColumn
    ? label
    : getLocalisedText({ t, value: label });

  const subText = _.get(column, "subText");
  const gradingPeriodLabel = getGradingPeriodLabel({ gradingPeriodId });
  const newSubText = _.isEmpty(subText)
    ? gradingPeriodLabel
    : `${gradingPeriodLabel}, ${t(subText)}`;

  return {
    ...column,
    id: `${id}_${gradingPeriodId}`,
    parent: id,
    level: 1,
    doesIdContainsMetaData: true,
    type: "CUSTOM",
    entity: `MULTIGRADING-${fieldUID}-${gradingPeriodId}`,
    parentLabel: newLabel,
    label: newSubText,
    style: styles,
    isGradingPeriodColumn: true,
  };
};

export const getUserConfiguredColumnsList = ({
  t,
  tableName,
  tableColumns,
  curriculumProgramId,
  configuredColumnsFromSettings,
  selectedChartViewByOption = undefined,
}) => {
  const { type: curriculumProgramType } = !_.isEmpty(curriculumProgramId)
    ? getCurriculumProgramDetails(curriculumProgramId)
    : {};

  const columnList = _.reduce(
    tableColumns,
    (resultantColumns, column) => {
      const columnChildren = _.get(column, "children");
      const isParent = _.get(column, "isParent", false);
      const isDynamicColumn = _.get(column, "isDynamicColumn", false);
      const configurationId = _.get(column, "configurationId"); //only in case of dynamic columns
      const id = !isDynamicColumn ? _.get(column, "id") : configurationId;
      const isSubTextLocalized = _.get(column, "isSubTextLocalized");
      const subText = _.get(column, "subText");

      // will be deprecated along with the perm PerformanceAnalyticsRevamp
      const isRevampPermEnabled = checkIsRevampPermEnabled();
      const isV2Column = _.get(column, "isV2Column");
      if (!isRevampPermEnabled && isV2Column) return resultantColumns;

      const { gradingPeriodIds } = getGradingPeriodProps();
      const multiGradingPeriodColumnIds = _.get(
        MULTIGRADING_COLUMN_CONSTANTS,
        tableName,
        []
      );
      const idToCheck = isDynamicColumn ? configurationId : id;
      const areMultipleGradingPeriodsSelected = _.size(gradingPeriodIds) > 1;
      const shouldDisableMultiGradingForPerf2p0 =
        getIsPerformanceInsights2p0Enabled() &&
        _.isEqual(idToCheck, "averageScore");

      const isMultiGradingPeriodColumn =
        !shouldDisableMultiGradingForPerf2p0 &&
        _.includes(multiGradingPeriodColumnIds, idToCheck) &&
        _.size(gradingPeriodIds) > 0;
      /*
      We will remove the _.size(gradingPeriodIds) > 0 check when the feature
       is rolled out to allschools
      */

      const hideInConfigurationPopup = _.get(
        column,
        "hideInConfigurationPopup",
        false
      );

      const curriculumPrograms = _.get(column, "curriculumPrograms");
      const isCurriculumDependent = !_.isUndefined(curriculumPrograms);
      const isValidForCurrentCurriculum = isCurriculumDependent
        ? _.includes(curriculumPrograms, curriculumProgramType)
        : true; //default value will be true for those columns which are not curriculum dependent

      const perm = _.get(column, "perm");
      if (perm) {
        const readPermFromCurriculumLevel = _.get(
          column,
          "readPermFromCurriculumLevel"
        );

        let isEnabled = true;
        if (readPermFromCurriculumLevel) {
          isEnabled = getSettingValue({
            name: perm,
            currentCurriculumProgramId: curriculumProgramId,
          });
        }

        if (!isEnabled) return resultantColumns;
        if (!ACLStore.can(perm)) {
          return resultantColumns;
        }
      }

      const chartViewByOptions = _.get(column, "chartViewByOptions", null);

      if (
        !_.isEqual(chartViewByOptions, null) &&
        !_.includes(chartViewByOptions, selectedChartViewByOption)
      )
        return resultantColumns;

      //Parent column should be rendered only if atleast one of its child is configured by the user.
      const isColumnConfigured = isParent
        ? _.some(columnChildren, child =>
            _.get(configuredColumnsFromSettings, child, false)
          )
        : _.get(configuredColumnsFromSettings, id, false);

      const fieldUID = _.get(column, "fieldUID");

      const parent = _.get(column, "parent");
      const level = _.get(column, "level", 0);

      // For subcolumns with hideInConfigurationPopup, check if parent is configured
      const shouldIncludeHiddenSubColumn =
        hideInConfigurationPopup && level > 0 && parent
          ? _.get(configuredColumnsFromSettings, parent, false)
          : hideInConfigurationPopup;

      if (
        (isColumnConfigured || shouldIncludeHiddenSubColumn) &&
        isValidForCurrentCurriculum
      ) {
        if (isMultiGradingPeriodColumn) {
          //Inserting children
          const children = [];
          const defaultStyles = _.get(column, "style");
          const childStyles = {
            ...defaultStyles,
            height: "36px",
          };
          const parentHeaderWidth =
            _.size(gradingPeriodIds) > 1
              ? undefined
              : _.get(defaultStyles, "width");
          /*because label wont come in center if we define a width to parent
           and if only one grading period is selected we dont wnt label to occupy
          more than required space */
          const parentStyles = {
            ...defaultStyles,
            width: parentHeaderWidth,
          };
          _.forEach(gradingPeriodIds, gradingPeriodId => {
            const columnListProps = getGradingPeriodColumnProps({
              t,
              id,
              gradingPeriodId,
              fieldUID,
              column,
              areMultipleGradingPeriodsSelected,
              styles: childStyles,
            });
            resultantColumns.push(columnListProps);
            const { id: childId } = columnListProps;
            children.push(childId);
          });
          //Inserting their parent
          const alignment = _.size(children) > 1 ? "center" : "right";
          resultantColumns.push({
            ...column,
            type: "CUSTOM",
            alignment,
            label: isDynamicColumn
              ? column.label
              : _.isEmpty(column.label)
              ? ""
              : getLocalisedText({ t, value: column.label }),
            subText: isSubTextLocalized
              ? subText
              : !_.isEmpty(subText)
              ? t(subText)
              : undefined,
            style: parentStyles,
            isParent: true,
            level: 0,
            children,
            sortType: undefined,
          });
        } else {
          const columnHeight = _.get(column, "columnHeight", "44px");
          resultantColumns.push({
            ...column,
            type: "CUSTOM",
            entity: fieldUID,
            label: isDynamicColumn
              ? column.label
              : _.isEmpty(column.label)
              ? ""
              : getLocalisedText({ t, value: column.label }),
            subText: isSubTextLocalized
              ? subText
              : !_.isEmpty(subText)
              ? t(subText)
              : undefined,
            style: { ...column.style, height: columnHeight },
          });
        }
      }

      return resultantColumns;
    },
    []
  );
  return columnList;
};

export const getXAxisInterval = ({ data, dateViewType }) => {
  const hasLessThan14Items = _.size(data) <= 13;
  if (hasLessThan14Items) return { xAxisInterval: 0 };

  return !["CURRENT_ACADEMIC_YEAR", "PREVIOUS_ACADEMIC_YEAR"].includes(
    dateViewType
  )
    ? {
        xAxisInterval: dateViewType === "LAST_7_DAYS" ? 0 : 2,
      }
    : {};
};

export const getHeaderTitle = ({ moduleName, path, t }) => {
  const headerTitle = _.get(HEADING_LABELS, `${moduleName}.${path}`, "");

  if (
    _.isEqual(moduleName, BEHAVIOUR_ANALYTICS) &&
    _.isEqual(path, "overview")
  ) {
    const localeParams = getNomenclatureLocaleParams({
      type: BEHAVIOUR_NOMENCLATURE_CONSTANTS.MODULE,
      convertToLowerCase: false,
    });
    return t(headerTitle, localeParams);
  }

  return headerTitle ? t(headerTitle) : headerTitle;
};

const generateCombinations = (obj, path = [], result = []) => {
  for (const key in obj) {
    const newPath = [...path, key];
    if (newPath.length > 3) result.push(newPath);
    generateCombinations(obj[key], newPath, result);
  }
  return result;
};

export const extractIdsFromUrlSearchParams = urlSearchParams => {
  if (!urlSearchParams) {
    urlSearchParams = new URLSearchParams(location.search);
  }

  const state = store?.getState();
  const selectedAcademicYearId =
    urlSearchParams.get("academicYear") || getSelectedAcademicYearId(state);

  const selectedCurriculumId = urlSearchParams.get("curriculum");
  const curriculumProgramIds = urlSearchParams.get("curriculumPrograms");

  const userInfo = getUserInfo();
  const curriculumProgramsInSequence = _.get(
    userInfo,
    "userCurriculumPrograms",
    []
  );

  // Create a map for quick lookup of the desired order
  const curriculumOrderMap = _.reduce(
    curriculumProgramsInSequence,
    (map, program, index) => {
      map[program.id] = index;
      return map;
    },
    {}
  );

  const curriculumProgramIdsFromParams = curriculumProgramIds
    ? _.split(curriculumProgramIds, QUERY_PARAM_DELIMITER)
    : [];

  // Sort the selected IDs based on the order defined in curriculumProgramsInSequence
  const selectedCurriculumProgramIds = _.sortBy(
    curriculumProgramIdsFromParams,
    id => curriculumOrderMap[id] ?? Infinity
  );

  const selectedGradeId = urlSearchParams.get("grade");
  const selectedGradingPeriodId = urlSearchParams.get("gradingPeriod");
  const gradingPeriodIds = urlSearchParams.get("gradingPeriods");
  const selectedGradingPeriodIds = gradingPeriodIds
    ? _.split(gradingPeriodIds, QUERY_PARAM_DELIMITER)
    : [];

  const subjectIds = urlSearchParams.get("subjects");
  const selectedSubjectIds = subjectIds
    ? _.split(subjectIds, QUERY_PARAM_DELIMITER)
    : [];

  const classIds = urlSearchParams.get("classes");
  const selectedClassIds = classIds
    ? _.split(classIds, QUERY_PARAM_DELIMITER)
    : [];

  const homeroomAdvisorIds = urlSearchParams.get("homeroomAdvisors");
  const selectedHomeroomAdvisorIds = homeroomAdvisorIds
    ? _.split(homeroomAdvisorIds, QUERY_PARAM_DELIMITER)
    : [];

  const {
    isStaffPerformanceModule,
    isClassDetailedView,
    isStaffDetailedView,
  } = getSubmoduleDetails();

  const { isAttendanceAnalytics } = getModuleNameDetails();

  let selectedGradeIds = _.compact([selectedGradeId]),
    courseQueryGradeInput = _.compact([selectedGradeId]),
    staffQueryGradeInput = _.compact([selectedGradeId]);

  if (isClassDetailedView) {
    const classGradesIds = urlSearchParams.get("classGrades");
    selectedGradeIds = classGradesIds
      ? _.split(classGradesIds, QUERY_PARAM_DELIMITER)
      : [];
  } else if (isStaffDetailedView) {
    const staffGradesIds = urlSearchParams.get("staffGrades");
    selectedGradeIds = staffGradesIds
      ? _.split(staffGradesIds, QUERY_PARAM_DELIMITER)
      : [];
  }

  if (isAttendanceAnalytics) {
    const gradeIds = urlSearchParams.get("grades");
    selectedGradeIds = gradeIds ? _.split(gradeIds, QUERY_PARAM_DELIMITER) : [];
  }
  if (isStaffPerformanceModule || isAttendanceAnalytics) {
    const gradeIds = urlSearchParams.get("grades");
    staffQueryGradeInput = gradeIds
      ? _.split(gradeIds, QUERY_PARAM_DELIMITER)
      : [];
  }

  if (isStaffPerformanceModule) {
    const staffGradesIds = urlSearchParams.get("staffGrades");
    courseQueryGradeInput = staffGradesIds
      ? _.split(staffGradesIds, QUERY_PARAM_DELIMITER)
      : [];
  }

  const dateViewType = urlSearchParams.get("dateViewType");
  const rangeStartDate = urlSearchParams.get("rangeStartDate");
  const rangeEndDate = urlSearchParams.get("rangeEndDate");
  const dateViewGradingPeriod = urlSearchParams.get("dateViewGradingPeriod");

  const selectedGradeCurriculumId = urlSearchParams.get(
    "selectedGradeCurriculumId"
  );
  const selectedClassCurriculumId = urlSearchParams.get(
    "selectedClassCurriculumId"
  );
  const selectedStudentCurriculumId = urlSearchParams.get(
    "selectedStudentCurriculumId"
  );

  return {
    selectedCurriculumId,
    selectedCurriculumProgramIds,
    selectedGradeIds,
    selectedSubjectIds,
    selectedClassIds,
    selectedHomeroomAdvisorIds,
    selectedGradingPeriodId,
    selectedGradingPeriodIds,
    selectedAcademicYearId,
    courseQueryGradeInput,
    staffQueryGradeInput,
    dateViewType,
    rangeStartDate,
    rangeEndDate,
    dateViewGradingPeriod,
    selectedGradeCurriculumId,
    selectedClassCurriculumId,
    selectedStudentCurriculumId,
  };
};

export const isMultiTermInsightsEnabled = curriculumProgramId => {
  if (getIsPerformanceInsights2p0Enabled()) return false;

  return getSettingValue({
    name: PERFORMANCE_MULTI_TERMS_PERM,
    currentCurriculumProgramId: curriculumProgramId,
  });
};

export const getGradingPeriodProps = () => {
  const {
    selectedCurriculumId,
    selectedGradingPeriodId,
    selectedGradingPeriodIds: gradingPeriodIdsFromUrl,
    selectedAcademicYearId,
  } = extractIdsFromUrlSearchParams();
  let selectedGradingPeriodIds = _.cloneDeep(gradingPeriodIdsFromUrl);

  const isMultiTermEnabled = isMultiTermInsightsEnabled(selectedCurriculumId);

  // sorting grading periods based on the dropdown order
  const gradingPeriodOptions = getGradingPeriodsMemoized({
    curriculumId: selectedCurriculumId,
    academicYearId: selectedAcademicYearId,
  });

  if (_.isEmpty(selectedGradingPeriodIds)) {
    selectedGradingPeriodIds = _.map(gradingPeriodOptions, "value");
  }

  const sortedGradingPeriodOptions = _.compact(
    _.map(gradingPeriodOptions, option =>
      _.find(selectedGradingPeriodIds, id => _.isEqual(id, option.key))
    )
  );

  // To-do: Currently storing both gradingPeriodId and gradingPeriodIds in the url params
  // since not all the queries support gradingPeriodIds yet
  return {
    // gradingPeriodId: isMultiTermEnabled ? undefined : selectedGradingPeriodId,
    isMultiTermEnabled,
    gradingPeriodId: selectedGradingPeriodId,
    /* gradingPeriodIds prop is used for query inputs. Handled the case where all gradingPeriodIds should be passed
    in query input when no gradingPeriodIds are selected */
    gradingPeriodIds: isMultiTermEnabled
      ? sortedGradingPeriodOptions
      : undefined,
    /* gradingPeriodIdsFromUrl is to maintain the filter state in the url */
    gradingPeriodIdsFromUrl: isMultiTermEnabled
      ? gradingPeriodIdsFromUrl
      : undefined,
  };
};

const getLearningCourseOptionsForBreadcrumb = ({
  curriculumId,
  selectedAcademicYearId,
  gradeIds,
}) => {
  const gradeId = _.first(gradeIds);

  return getLearningCourseOptions({
    curriculumId,
    gradeId,
    selectedAcademicYearId,
  });
};

const getSegmentLabel = ({ entityName, entityId, urlSearchParams }) => {
  const {
    selectedCurriculumId,
    selectedGradeIds,
    courseQueryGradeInput,
    staffQueryGradeInput,
    selectedAcademicYearId,
  } = extractIdsFromUrlSearchParams(urlSearchParams);

  const queryFunctions = {
    gradeId: getGradeOptions,
    subjectId: getSubjectOptionsMemoized,
    classId: getCourseOptions,
    staffId: getStaffOptions,
    studentId: getStudentOptions,
    learningCourseId: getLearningCourseOptionsForBreadcrumb,
  };

  const gradeIdsMap = {
    classId: courseQueryGradeInput,
    staffId: staffQueryGradeInput,
  };

  const entities = queryFunctions[entityName]({
    curriculumId: selectedCurriculumId,
    selectedAcademicYearId,
    gradeIds: gradeIdsMap[entityName] || selectedGradeIds,
  });
  const selectedEntity = _.find(entities, { value: entityId });
  const label = _.get(selectedEntity, "label", "");
  return label;
};

const getSegmentLabelFromEntityQuery = ({ entityName, entityId }) => {
  const queryFunctions = {
    gradeId: () =>
      getEntityOptions({ entityType: ANALYTICS_ENTITY_TYPE_ENUMS.grade }),
    studentId: () =>
      getEntityOptions({ entityType: ANALYTICS_ENTITY_TYPE_ENUMS.student }),
    categoryId: () =>
      getBehaviourCategoryEntityOptions({ withCurriculumFilter: true }),
    classId: () =>
      getEntityOptions({ entityType: ANALYTICS_ENTITY_TYPE_ENUMS.course }),
  };

  const entities = queryFunctions[entityName]();
  const selectedEntity = _.find(entities, { value: entityId });
  const label = _.get(selectedEntity, "label", "");
  return label;
};

const generateBreadCrumbs = ({
  t,
  breadCrumbRoutes,
  params,
  moduleRoute,
  urlSearchParams,
  shouldUseEntityQuery,
}) => {
  const breadCrumbs = _.map(breadCrumbRoutes, route => {
    let depth = 1;
    let label, id;
    // If it's not an Id route, fetch the label from HEADING_LABELS
    if (!_.includes(route, "Id")) {
      const moduleName = ROUTE_MODULE_MAPPING[moduleRoute];
      label = getHeaderTitle({ moduleName, path: route, t });
    } else {
      // If it's an Id route, fetch the label from query cache
      const routeParts = route.split("/");
      depth = _.size(routeParts);

      // Find the route with "Id"
      const entityName = _.find(routeParts, route => _.includes(route, "Id"));

      // Get the entity id from params
      id = _.get(params, entityName, "");
      label = shouldUseEntityQuery
        ? getSegmentLabelFromEntityQuery({ entityName, entityId: id })
        : getSegmentLabel({ entityName, entityId: id, urlSearchParams });
    }
    return { label, depth, id };
  });
  return breadCrumbs;
};

export const generateBreadCrumbState = ({
  t,
  params,
  routes,
  urlSearchParams,
  shouldUseEntityQuery,
}) => {
  // Generate path array from routes
  // Ex: ["toddle-insights", "performance", "subject", "subjectId"]
  const currentPathArray = _.reduce(
    routes,
    (paths, { path }) => {
      if (
        _.isEqual(path, "toddle-insights") ||
        _.includes(paths, "toddle-insights")
      ) {
        // Remove ":" from path
        paths.push(path.startsWith(":") ? path.replace(":", "") : path);
      }
      return paths;
    },
    []
  );

  // Generate all possible path array combinations from analytics routemap
  const analyticsPathArray = generateCombinations(ROUTE_MAP);

  const currentRouteArray = _.find(analyticsPathArray, route =>
    _.isEqual(_.join(route, "/"), _.join(currentPathArray, "/"))
  );

  let breadCrumbRoutes, moduleRoute;
  // If current route is present in analytics routemap, it means it is a valid route
  if (currentRouteArray) {
    moduleRoute = _.get(currentRouteArray, "1");
    // Remove first two paths since they are not shown in bread crumbs
    breadCrumbRoutes = _.slice(currentRouteArray, 2);
  }

  return !_.isEmpty(breadCrumbRoutes)
    ? generateBreadCrumbs({
        t,
        breadCrumbRoutes,
        params,
        moduleRoute,
        urlSearchParams,
        shouldUseEntityQuery,
      })
    : [];
};

export const getViewByDropdownOptions = ({ t }) => {
  return [
    {
      key: "average_ib_dp_total",
      label: t("toddleAnalytics:ib_dp_total"),
    },
    {
      key: "average_ib_myp_total",
      label: t("toddleAnalytics:ib_myp_total"),
    },
    {
      key: "ib_dp_grade",
      label: t("toddleAnalytics:ib_grade"),
    },
    {
      key: "average_ib_dp_grade",
      label: t("toddleAnalytics:ib_grade"),
    },
    {
      key: "ib_myp_grade",
      label: t("toddleAnalytics:ib_grade"),
    },
    {
      key: "average_ib_myp_grade",
      label: t("toddleAnalytics:ib_grade"),
    },
    {
      key: "average_score",
      label: t("toddleAnalytics:score"),
    },
    {
      key: "ib_dp_total",
      label: t("toddleAnalytics:ib_dp_total"),
    },
    {
      key: "ib_myp_total",
      label: t("toddleAnalytics:ib_myp_total"),
    },
    {
      key: "score",
      label: t("toddleAnalytics:score"),
    },
    {
      key: "overall",
      label: t("toddleAnalytics:overall_performance"),
    },
    {
      key: "subject",
      label: t("toddleAnalytics:subject_wise"),
    },
    {
      key: "grade_scale",
      label: t("toddleAnalytics:grade_scale"),
    },
    {
      key: "grade",
      label: t("common:grade"),
      //maintaing both of them separately to avoid confusion
      //tableSuffix is used for forming the table name
      //viewTypeKey is passed in the query
      tableSuffix: "GRADE",
      viewTypeKey: "GRADE",
    },
    {
      key: "learning_course",
      label: t("common:dp_course"),
      tableSuffix: "LEARNING_COURSE",
      viewTypeKey: "LEARNING_COURSE",
    },
    {
      key: "course",
      label: t("common:class"),
      tableSuffix: "CLASS",
      viewTypeKey: "COURSE",
    },
    {
      key: "staff",
      label: t("common:staff"),
      tableSuffix: "STAFF",
      viewTypeKey: "STAFF",
    },
    {
      key: "student",
      label: t("common:student"),
      tableSuffix: "STUDENT",
      viewTypeKey: "STUDENT",
    },
    {
      key: "family",
      label: t("common:family"),
      tableSuffix: "FAMILY",
      viewTypeKey: "FAMILY",
    },
  ];
};

const getDefaultOrderByForUser = ({ entityType }) => {
  const isUserMiddleNameEnabled = ACLStore.can(ENABLE_USER_MIDDLE_NAME);
  const isStudentMiddleNameEnabled = ACLStore.can(ENABLE_STUDENT_MIDDLE_NAME);

  switch (entityType) {
    case "STUDENT": {
      if (isStudentMiddleNameEnabled || isUserMiddleNameEnabled) return "NAME";
      return "FIRST_NAME";
    }
    case "PARENT":
    case "FAMILY":
    case "STAFF": {
      if (isUserMiddleNameEnabled) return "NAME";
      return "FIRST_NAME";
    }
    default:
      return "FIRST_NAME";
  }
};

export const getDefaultSortOption = ({ entityType, moduleType, isDP }) => {
  const defaultOrderByForUser = getDefaultOrderByForUser({ entityType });

  if (_.isEqual(moduleType, ENGAGEMENT_ANALYTICS)) {
    return { ...DEFAULT_SORT_OPTION, orderBy: defaultOrderByForUser };
  }
  if (
    _.includes(
      [PERFORMANCE_ANALYTICS, MODULE_USAGE_ANALYTICS, ATTENDANCE_ANALYTICS],
      moduleType
    )
  ) {
    const sortOptions = {
      orderByDirection: "ASC",
    };
    switch (entityType) {
      case "STUDENT":
      case "FAMILY":
      case "STAFF":
        return {
          orderBy: defaultOrderByForUser,
          ...sortOptions,
        };
      case "GRADE": {
        return {
          orderBy: _.includes(
            [MODULE_USAGE_ANALYTICS, ATTENDANCE_ANALYTICS],
            moduleType
          )
            ? "DISPLAY_SEQUENCE_GLOBAL"
            : "DISPLAY_SEQUENCE",
          ...sortOptions,
        };
      }
      case "DATE": {
        return {
          orderBy: entityType,
          orderByDirection: "DESC",
        };
      }
      case "LEARNING_COURSE": {
        return {
          orderBy: "LEARNING_COURSE",
          ...sortOptions,
        };
      }
      case "SUBJECT": {
        return {
          orderBy: isDP ? "DP_SUBJECT" : "SUBJECT",
          ...sortOptions,
        };
      }
      default:
        return {
          orderBy: entityType,
          ...sortOptions,
        };
    }
  }
  if (_.isEqual(moduleType, BEHAVIOUR_ANALYTICS)) {
    const sortOptions = {
      orderByDirection: "ASC",
    };
    switch (entityType) {
      case "STUDENT":
        return { ...DEFAULT_SORT_OPTION, orderBy: defaultOrderByForUser };
      case "BEHAVIOUR_CATEGORY":
        return {
          orderBy: entityType,
          ...sortOptions,
        };
      case "GRADE": {
        return {
          orderBy: "DISPLAY_SEQUENCE",
          ...sortOptions,
        };
      }
      case "INCIDENT": {
        return {
          orderBy: "INCIDENT_CREATED_AT",
          orderByDirection: "DESC",
        };
      }
      default:
        return {};
    }
  }
  return {};
};

export const getDefaultFilters = ({ moduleType, entityType, isDP }) => {
  switch (moduleType) {
    case ENGAGEMENT_ANALYTICS:
    case PERFORMANCE_ANALYTICS:
    case BEHAVIOUR_ANALYTICS:
    case MODULE_USAGE_ANALYTICS:
    case ATTENDANCE_ANALYTICS:
      return {
        searchText: "",
        sortOptions: getDefaultSortOption({ moduleType, entityType, isDP }),
      };
    default:
      return null;
  }
};

export const getTableFiltersFromRedux = ({
  tableState,
  moduleType,
  entityType,
  isDP,
}) => {
  const tableFilters = _.cloneDeep(
    getDefaultFilters({ moduleType, entityType, isDP })
  );
  const searchTextFromRedux = _.get(tableState, "searchText");
  const sortOptionsFromRedux = _.get(tableState, "sortOptions");

  if (!_.isEmpty(searchTextFromRedux)) {
    tableFilters["searchText"] = searchTextFromRedux;
  }
  if (!_.isEmpty(sortOptionsFromRedux)) {
    tableFilters["sortOptions"] = sortOptionsFromRedux;
  }

  return tableFilters;
};

export const getUpdatedSortOptions = ({
  sortDirection,
  menuItemEntity,
  moduleType,
}) => {
  if (_.isEmpty(menuItemEntity)) {
    return getDefaultSortOption(moduleType);
  }

  /* we need to pass meta data as well to BE for dynamic columns. So we are storing them
  as - separated values
  */
  const splittedSortOptions = _.split(menuItemEntity, "-");
  const orderBy = _.get(splittedSortOptions, "0");
  if (_.isEqual(orderBy, "CATEGORY")) {
    const categoryId = _.get(splittedSortOptions, "1");
    const categoryValueType = _.get(splittedSortOptions, "2");
    const academicTermId = _.get(splittedSortOptions, "3", undefined);

    return {
      orderBy,
      orderByDirection: sortDirection,
      metadata: {
        categoryId,
        categoryValueType,
        academicTermId,
      },
    };
  }

  if (_.isEqual(orderBy, "MULTIGRADING")) {
    const fieldUID = _.get(splittedSortOptions, "1");
    const gradingPeriodId = _.get(splittedSortOptions, "2");

    return {
      orderBy: fieldUID,
      stringOrderBy: `${fieldUID}__${gradingPeriodId}`,
      orderByDirection: sortDirection,
    };
  }

  if (
    _.isEqual(orderBy, "BEHAVIOUR_CATEGORY") &&
    _.size(splittedSortOptions) > 1 // BEHAVIOUR_CATEGORY has different purposes
  ) {
    const behaviourCategoryId = _.get(splittedSortOptions, "1");

    return {
      orderBy,
      orderByDirection: sortDirection,
      metadata: {
        behaviourCategoryId,
      },
    };
  }

  if (
    _.isEqual(orderBy, "CATEGORY_WISE_ATTENDANCE_PERCENTAGE") &&
    _.size(splittedSortOptions) > 1
  ) {
    const fieldUID = _.get(splittedSortOptions, "0");
    const categoryId = _.get(splittedSortOptions, "1");

    return {
      orderBy: fieldUID,
      stringOrderBy: `${fieldUID}__${categoryId}`,
      orderByDirection: sortDirection,
    };
  }

  return {
    orderBy: menuItemEntity,
    orderByDirection: sortDirection,
  };
};

/**
 *
 *  Input:
 * tableDate: [
 *  {name: "Analytics", subjects:["Maths", "Science"], score: [10, 20]  }
 * ],
 * multivaluedfields: ["subjects", "score"]
 * ExpectedOutput:[
 *  {name: "Analytics", subjects: "Maths", score: 10, rowSpan: {name: 2}},
 * {subjects: "Science", score: 20}
 * ]
 */

export const getFormattedDataForMultirowTables = ({
  tableData,
  multiValuedFields,
  includeIdInEveryRow,
}) => {
  const result = [];
  // Iterate over each object and spread out the multi-valued fields
  _.forEach(tableData, item => {
    //Calculating maximumrow span for that field
    let maxLength = 1;

    _.forEach(multiValuedFields, field => {
      const value = _.get(item, field);
      if (!_.isUndefined(value) && _.isArray(value)) {
        maxLength = Math.max(maxLength, _.size(value));
      }
    });
    for (let i = 0; i < maxLength; i++) {
      const newRow = { rowSpan: {}, cellStyles: {} };

      if (_.isEqual(i, maxLength - 1)) {
        newRow["isEndOfMultiRow"] = true;
      }

      if (includeIdInEveryRow) {
        newRow["id"] = `${_.get(item, "id")}_${i}`;
      }

      // Include single-valued fields only the first time
      if (_.isEqual(i, 0)) {
        const allFields = _.keys(item);
        _.forEach(allFields, field => {
          if (!_.includes(multiValuedFields, field)) {
            const cellStyles = _.get(item, [field, "cellStyles"]);
            if (_.isEmpty(cellStyles)) {
              newRow[field] = _.get(item, field);
            } else {
              newRow[field] = _.get(item, [field, "value"]);
              newRow["cellStyles"][field] = cellStyles;
            }
            newRow["rowSpan"][field] = maxLength;
          }
        });
      }

      // Add multi-valued fields only if they are not undefined
      _.forEach(multiValuedFields, field => {
        const ithRowValueOfMultiValuedField = _.get(item, [field, i]);
        if (!_.isUndefined(ithRowValueOfMultiValuedField)) {
          const cellStyles = _.get(ithRowValueOfMultiValuedField, "cellStyles");
          if (_.isEmpty(cellStyles)) {
            newRow[field] = ithRowValueOfMultiValuedField;
          } else {
            newRow[field] = _.get(ithRowValueOfMultiValuedField, "value");
            newRow["cellStyles"][field] = cellStyles;
          }
        } else {
          newRow[field] = "";
        }
      });

      // Add the row to the result if it has any properties
      // > 1 because we are adding rowSpan property by default
      if (_.size(_.keys(newRow)) > 1) {
        result.push(newRow);
      }
    }
  });

  return result;
};

export const getMaxYAxisValueWithEqualTickDistribution = (value, tickCount) => {
  if (_.isEqual(value, 0)) return tickCount;
  const stepSize = Math.ceil(value / tickCount);
  return stepSize * tickCount;
};

export const getTableStates = ({
  isLoading,
  isDataEmpty,
  searchInputValue,
  showErrorState,
}) => {
  const showEmptyState = !isLoading && isDataEmpty;
  const noDataState = showEmptyState && _.isEmpty(searchInputValue);
  const showFilterSection = !showErrorState && !noDataState;

  return { showEmptyState, noDataState, showFilterSection };
};

const getStudentVsAvgIbGradeFormattedGraphData = (t, graphData) => {
  if (!_.isArray(graphData)) return [];

  const gradeWiseStudentCount = _.reduce(
    graphData,
    (res, data) => {
      const ibGrade = _.toNumber(_.get(data, "xAxis", ""));
      const studentCount = _.toNumber(_.get(data, "yAxis.0.value", 0));

      if (!res[ibGrade]) res[ibGrade] = studentCount;
      else res[ibGrade] += studentCount;

      return res;
    },
    {}
  );

  return _.times(MAX_IB_DP_GRADE, i => {
    return {
      ibGrade: `${t("toddleAnalytics:ib_grade")} ${i + 1}`,
      studentCount: gradeWiseStudentCount[i + 1] || 0,
    };
  });
};

const getStudentVsAvgIbGradeFormattedGraphDataForMultiTerm = ({
  graphData,
  gradingPeriodIds,
}) => {
  if (!_.isArray(graphData)) return [];

  const formattedGraphData = _.map(graphData, data => {
    const ibGrade = _.toNumber(_.get(data, "xAxis", ""));
    const yAxisData = _.get(data, "yAxis", []);

    const result = _.reduce(
      yAxisData,
      (res, curr) => {
        const gradingPeriodId = _.split(_.get(curr, "key"), "__")?.[1];
        const gradingPeriodLabel = getGradingPeriodLabel({ gradingPeriodId });

        const key = `studentCount_${gradingPeriodLabel}`;
        if (!res[key]) res[key] = 0;

        res[`studentCount_${gradingPeriodLabel}`] += _.toNumber(
          _.get(curr, "value", 0)
        );
        return res;
      },
      {}
    );

    // add ibGarde to result
    result["ibGrade"] = ibGrade;

    // add the remaining gradingPeriods to result
    _.forEach(gradingPeriodIds, gradingPeriodId => {
      const gradingPeriodLabel = getGradingPeriodLabel({ gradingPeriodId });
      const key = `studentCount_${gradingPeriodLabel}`;
      if (!result[key]) {
        result[key] = 0;
      }
    });

    return result;
  });

  // add the remaining ib grades to result
  _.times(MAX_IB_DP_GRADE, i => {
    // if the ib grade is already present, then return
    const ibGrade = i + 1;
    if (
      _.find(formattedGraphData, data => _.get(data, "ibGrade", "") === ibGrade)
    ) {
      return;
    }

    // create a new result for the ib grade
    const result = { ibGrade };
    _.forEach(gradingPeriodIds, gradingPeriodId => {
      const gradingPeriodLabel = getGradingPeriodLabel({ gradingPeriodId });
      const key = `studentCount_${gradingPeriodLabel}`;
      result[key] = 0;
    });

    // append it to formattedGraphData
    formattedGraphData.push(result);
  });

  return _.orderBy(formattedGraphData, "ibGrade");
};

const getStudentVsScoresFormattedGraphData = graphData => {
  if (!_.isArray(graphData)) return [];

  const countMap = {};
  _.forEach(SCORE_RANGES, range => {
    countMap[range.label] = 0;
  });

  _.forEach(graphData, data => {
    const score = _.get(data, "xAxis", "");
    const yAxisValue = _.get(data, "yAxis.0.value", []);
    const studentCount = _.isNumber(yAxisValue)
      ? yAxisValue
      : _.toNumber(yAxisValue);

    const range = _.find(
      SCORE_RANGES,
      range => score >= range.min && score <= range.max
    );
    if (range) {
      countMap[range.label] += _.toNumber(studentCount);
    }
  });

  const result = _.map(SCORE_RANGES, range => {
    const rangeLabel = range?.label;

    return {
      score: rangeLabel,
      studentCount: countMap[rangeLabel],
    };
  });

  return _.orderBy(result, "score");
};

const getStudentVsScoresFormattedGraphDataForMultiTerm = ({
  graphData,
  gradingPeriodIds,
}) => {
  if (!_.isArray(graphData)) return [];

  // create a count map
  const countMap = {};
  _.forEach(gradingPeriodIds, gradingPeriodId => {
    _.forEach(SCORE_RANGES, range => {
      const gradingPeriodLabel = getGradingPeriodLabel({ gradingPeriodId });

      if (!countMap[gradingPeriodLabel]) {
        countMap[gradingPeriodLabel] = {};
      }

      countMap[gradingPeriodLabel][range.label] = 0;
    });
  });

  // count the number of students in each range
  _.forEach(graphData, data => {
    const score = _.get(data, "xAxis", "");
    const yAxisData = _.get(data, "yAxis", []);

    const range = _.find(
      SCORE_RANGES,
      range => score >= range.min && score <= range.max
    );

    _.forEach(yAxisData, data => {
      const gradingPeriodId = _.split(_.get(data, "key"), "__")?.[1];
      const gradingPeriodLabel = getGradingPeriodLabel({ gradingPeriodId });
      const count = _.toNumber(_.get(data, "value", 0));

      if (range) {
        // initialize the grading period label if not present
        if (!countMap[gradingPeriodLabel]) {
          countMap[gradingPeriodLabel] = {};
        }

        if (countMap[gradingPeriodLabel][range.label]) {
          countMap[gradingPeriodLabel][range.label] += count;
        } else {
          countMap[gradingPeriodLabel][range.label] = count; // Correctly initialize the range label
        }
      }
    });
  });

  // format to final result
  const result = _.map(SCORE_RANGES, range => {
    const currentResult = { score: range.label };

    return _.reduce(
      gradingPeriodIds,
      (res, gradingPeriodId) => {
        const gradingPeriodLabel = getGradingPeriodLabel({ gradingPeriodId });

        let count = 0;
        if (countMap[gradingPeriodLabel]) {
          count = countMap[gradingPeriodLabel][range.label];
        }

        res[`studentCount_${gradingPeriodLabel}`] = count;
        return res;
      },
      currentResult
    );
  });

  return _.orderBy(result, "score");
};

const getSingleTermGraphDataForStudentInSubjectOrClass = ({
  t,
  graphData,
  viewByOption,
  curriculumDetails = {},
}) => {
  const { isUBD } = curriculumDetails;

  if (isUBD) return getStudentVsScoresFormattedGraphData(graphData);

  if (viewByOption === "score")
    return getStudentVsScoresFormattedGraphData(graphData);

  return getStudentVsAvgIbGradeFormattedGraphData(t, graphData);
};

const getStudentVsGradeScaleFormattedGraphData = graphData => {
  if (!_.isArray(graphData)) return [];

  return _.map(graphData, data => {
    const gradeScale = _.get(data, "xAxis", "");
    const studentCount = _.toNumber(_.get(data, "yAxis.0.value", 0));

    return {
      gradeScale,
      studentCount,
    };
  });
};

export const getStudentPerformanceInSubjectOrClassFormattedGraphData = ({
  t,
  graphData,
  viewByOption,
  gradingPeriodIds,
  isMultiTermEnabled,
  curriculumDetails = {},
}) => {
  if (
    !getIsPerformanceInsights2p0Enabled() &&
    !isMultiTermEnabled &&
    _.isEmpty(graphData)
  )
    return [];

  // Handle grade scale view option
  if (viewByOption === "grade_scale") {
    if (isMultiTermEnabled) {
      return [];
    }
    return getStudentVsGradeScaleFormattedGraphData(graphData);
  }

  if (!isMultiTermEnabled) {
    return getSingleTermGraphDataForStudentInSubjectOrClass({
      t,
      graphData,
      viewByOption,
      curriculumDetails,
    });
  }

  const { isUBD } = curriculumDetails;

  if (isUBD)
    return getStudentVsScoresFormattedGraphDataForMultiTerm({
      graphData,
      gradingPeriodIds,
    });

  if (viewByOption === "score")
    return getStudentVsScoresFormattedGraphDataForMultiTerm({
      graphData,
      gradingPeriodIds,
    });

  return getStudentVsAvgIbGradeFormattedGraphDataForMultiTerm({
    t,
    graphData,
    gradingPeriodIds,
  });
};

export const getLabelValueForSingleSelectFilter = ({ options, key }) => {
  const selectedOption = _.find(options, { key });
  return _.get(selectedOption, "label", "");
};

const arrangeGradingPeriodsInSequence = fieldValues => {
  const {
    selectedAcademicYearId,
    selectedCurriculumId,
  } = extractIdsFromUrlSearchParams();
  const gradingPeriodOptions = getGradingPeriodsMemoized({
    curriculumId: selectedCurriculumId,
    academicYearId: selectedAcademicYearId,
  });

  return _.compact(
    _.map(gradingPeriodOptions, option =>
      _.find(fieldValues, { id: option.key })
    )
  );
};

const getOverviewCardTermWiseValues = ({
  valueKey,
  overviewMetricsFieldValueMap,
  isPercentageValue,
  isMultiTermEnabled,
}) => {
  if (!isMultiTermEnabled) return [];
  const values = [];
  _.forEach(overviewMetricsFieldValueMap, (value, key) => {
    const splittedField = _.split(key, "__");
    const fieldName = _.get(splittedField, "0");
    const gradingPeriodId = _.get(splittedField, "1");

    const isNullValue = _.isEqual(value, "null");
    const rawValue = trimNumber(_.toNumber(value)) || DEFAULT_VALUE_FOR_NO_DATA;

    if (_.isEqual(fieldName, valueKey)) {
      values.push({
        id: gradingPeriodId,
        label: getGradingPeriodLabel({ gradingPeriodId }),
        abbreviation: getGradingPeriodAbbreviation({ gradingPeriodId }),
        rawValue,
        value: isNullValue ? "-" : rawValue + (isPercentageValue ? "%" : ""),
        isNullValue,
      });
    }
  });

  return arrangeGradingPeriodsInSequence(values);
};

const getChartPropsForOverviewCard = ({
  valueKey,
  overviewMetrics,
  overviewMetricsFieldValueMap,
  isPercentageValue,
  isMultiTermEnabled,
  hasInverseTrend,
}) => {
  const termWiseValues = getOverviewCardTermWiseValues({
    valueKey,
    overviewMetrics,
    overviewMetricsFieldValueMap,
    isPercentageValue,
    isMultiTermEnabled,
  });

  if (_.isEmpty(termWiseValues)) return {};
  if (_.size(termWiseValues) < 2) return { data: termWiseValues };

  const chartType = "areaChart";

  // Filter terms with null values
  const filteredTermWiseValues = _.filter(
    termWiseValues,
    ({ isNullValue }) => !isNullValue
  );

  let graphColor = "var(--teal-500)";

  if (_.size(filteredTermWiseValues) > 1) {
    const firstItem = _.first(filteredTermWiseValues);
    const lastItem = _.last(filteredTermWiseValues);
    const firstItemValue = _.get(firstItem, "rawValue");
    const lastItemValue = _.get(lastItem, "rawValue");

    const isReversingTrend = hasInverseTrend
      ? lastItemValue <= firstItemValue
      : lastItemValue >= firstItemValue;

    graphColor = isReversingTrend ? "var(--teal-500)" : "var(--red-500)";
  }

  return {
    version: "2.0",
    chartType,
    height: 80,
    xAxisTickMargin: -2,
    xAxisHeight: 20,
    hideTooltip: true,
    data: termWiseValues,
    hideYAxis: true,
    hideXAxisLine: true,
    hideCartesianGrid: true,
    filterNullValues: true,
    xAxisDataKey: "abbreviation",
    legendProps: { hideLegend: true },
    xPadding: {},
    accessibilityLayer: false,
    areaOptions: [
      {
        activeDot: true,
        dataKey: "rawValue",
        stroke: graphColor,
        fill: graphColor,
        dot: true,
      },
    ],
  };
};

export const createOverviewCardData = ({
  t,
  valueKey,
  tagKey,
  totalKey,
  trendParams,
  subtitle,
  tooltipHeading,
  tooltipText,
  overviewMetrics,
  overviewMetricsFieldValueMap,
  isPercentageValue = false,
  isMultiTermEnabled,
  maxValue,
  fill,
  stroke,
  duration,
  showOnlyValueDifference,
  hasInverseTrend = false,
  getPastValueFromMetadata,
  showNullAsDash = true,
  compareLocaleKey,
  compareValueKey,
}) => {
  overviewMetricsFieldValueMap =
    overviewMetricsFieldValueMap ||
    getOverviewMetricsFieldValueMap(
      overviewMetrics,
      isMultiTermEnabled ? "stringField" : "field",
      getPastValueFromMetadata
    );

  const stringValue = isNaN(_.toNumber(overviewMetricsFieldValueMap[valueKey]));

  const cardValue = stringValue
    ? overviewMetricsFieldValueMap[valueKey]
    : _.toNumber(overviewMetricsFieldValueMap[valueKey]);
  const tagValue = overviewMetricsFieldValueMap[tagKey];

  const showDashInValue =
    showNullAsDash && (_.isNaN(cardValue) || cardValue === "null");

  trendParams = !showDashInValue &&
    (trendParams || getPastValueFromMetadata) && {
      activeFieldValue: valueKey,
      pastFieldValue: `${valueKey}_PAST`,
      isPercentageValue,
      showOnlyValueDifference,
      overviewMetricsFieldValueMap,
      ...trendParams,
    };

  const total = !showDashInValue
    ? _.toNumber(overviewMetricsFieldValueMap[totalKey])
    : undefined; // having undefined check in OverviewBox

  const compareValue = !showDashInValue
    ? _.toNumber(overviewMetricsFieldValueMap[compareValueKey])
    : undefined;

  const tooltipHeadingLocale = tooltipHeading
    ? getLocalisedText({ t, value: tooltipHeading })
    : undefined;

  const tooltipTextLocale = tooltipText
    ? getLocalisedText({ t, value: tooltipText })
    : undefined;

  const subtitleLocale = subtitle
    ? getLocalisedText({ t, value: subtitle })
    : undefined;

  const chartProps = getChartPropsForOverviewCard({
    maxValue,
    valueKey,
    overviewMetrics,
    overviewMetricsFieldValueMap,
    isPercentageValue,
    isMultiTermEnabled,
    fill,
    stroke,
    hasInverseTrend,
  });

  return {
    value: showDashInValue
      ? "-"
      : ((stringValue ? cardValue : trimNumber(cardValue)) ||
          DEFAULT_VALUE_FOR_NO_DATA) + (isPercentageValue ? "%" : ""),
    total,
    tagValue,
    compareValue,
    subtitle: subtitleLocale,
    ...(tooltipHeading
      ? {
          helpIconTooltipProps: {
            tooltipHeading: tooltipHeadingLocale,
            tooltipText: tooltipTextLocale,
          },
        }
      : {}),
    ...(trendParams
      ? {
          trendProps: {
            trend: getOverviewCardTrendSymbol({
              ...trendParams,
              overviewMetricsFieldValueMap,
            }),
            trendValue: getOverviewCardTrendValue({
              ...trendParams,
              overviewMetricsFieldValueMap,
            }),
            duration: trendParams?.duration,
          },
        }
      : {}),
    chartProps,
    duration,
    compareLocaleKey,
  };
};

export const getMeanIBGradeAndScoreVariableProps = ({
  t,
  viewByOption,
  calculateYDomainMin,
  isRevampPermEnabled,
}) => {
  const isScoreSelected =
    viewByOption === "score" || viewByOption === "average_score";

  const IB_GRADE_TICK_COUNT = 8;
  const SCORE_TICK_COUNT = 6;
  const IB_GRADE_MAX_Y_AXIS_VALUE = 7;
  const SCORE_MAX_Y_AXIS_VALUE = 100;

  let yAxisLabel;
  if (isRevampPermEnabled) {
    yAxisLabel = isScoreSelected
      ? t("toddleAnalytics:score_with_percent")
      : t("toddleAnalytics:ib_grade");
  } else {
    yAxisLabel = isScoreSelected
      ? t("toddleAnalytics:mean_score_percent")
      : t("toddleAnalytics:mean_ib_grade");
  }

  const tickCount = isScoreSelected ? SCORE_TICK_COUNT : IB_GRADE_TICK_COUNT;
  const isYAxisValueInPercentage = isScoreSelected ? true : false;

  const maxYAxisValue = isScoreSelected
    ? SCORE_MAX_Y_AXIS_VALUE
    : IB_GRADE_MAX_Y_AXIS_VALUE;

  const yDomainMin = calculateYDomainMin
    ? -1 * (maxYAxisValue / 700)
    : undefined;

  return {
    yAxisLabel,
    tickCount,
    isYAxisValueInPercentage,
    maxYAxisValue,
    yDomainMin,
  };
};

export const getIBGradeAndScoreVariableProps = ({ t, viewByOption }) => {
  const showScoreOnYAxis =
    viewByOption === "score" || viewByOption === "average_score";

  const xAxisLabel = showScoreOnYAxis
    ? t("toddleAnalytics:scores")
    : t("toddleAnalytics:ib_grades");

  const xAxisDataKey = showScoreOnYAxis ? "score" : "ibGrade";

  return {
    xAxisLabel,
    xAxisDataKey,
  };
};

export const getCriteriaWiseOverviewDataForMYP = ({
  t,
  overviewMetrics,
  isMultiTermEnabled,
}) => {
  const overviewMetricsFieldValueMap = getOverviewMetricsFieldValueMap(
    overviewMetrics,
    isMultiTermEnabled ? "stringField" : "field"
  );
  const params = { t, overviewMetricsFieldValueMap, isMultiTermEnabled };

  return [
    createOverviewCardData({
      ...params,
      valueKey: "AVERAGE_CRITERIA_A_GRADE",
      subtitle: (
        <Trans i18nKey="toddleAnalytics:criteria_a_mean_grade_bold">
          <span className="text-label-3">Criteria A:</span>
          <span>Mean Grade</span>
        </Trans>
      ),
      maxValue: MAX_CRITERIA_GRADE,
    }),
    createOverviewCardData({
      ...params,
      valueKey: "AVERAGE_CRITERIA_B_GRADE",
      subtitle: (
        <Trans i18nKey="toddleAnalytics:criteria_b_mean_grade_bold">
          <span className="text-label-3">Criteria B:</span>
          <span>Mean Grade</span>
        </Trans>
      ),
      maxValue: MAX_CRITERIA_GRADE,
    }),
    createOverviewCardData({
      ...params,
      valueKey: "AVERAGE_CRITERIA_C_GRADE",
      subtitle: (
        <Trans i18nKey="toddleAnalytics:criteria_c_mean_grade_bold">
          <span className="text-label-3">Criteria C:</span>
          <span>Mean Grade</span>
        </Trans>
      ),
      maxValue: MAX_CRITERIA_GRADE,
    }),
    createOverviewCardData({
      ...params,
      valueKey: "AVERAGE_CRITERIA_D_GRADE",
      subtitle: (
        <Trans i18nKey="toddleAnalytics:criteria_d_mean_grade_bold">
          <span className="text-label-3">Criteria D:</span>
          <span>Mean Grade</span>
        </Trans>
      ),
      maxValue: MAX_CRITERIA_GRADE,
    }),
  ];
};

export const getModuleName = path => {
  if (_.includes(path, "performance")) {
    return PERFORMANCE_ANALYTICS;
  }
  if (_.includes(path, "engagement")) {
    return ENGAGEMENT_ANALYTICS;
  }
  if (_.includes(path, "behaviour")) {
    return BEHAVIOUR_ANALYTICS;
  }
  if (_.includes(path, "module-usage")) {
    return MODULE_USAGE_ANALYTICS;
  }
  if (_.includes(path, "attendance")) {
    return ATTENDANCE_ANALYTICS;
  }
  return null;
};

export const getSubmoduleName = () => {
  const path = location.pathname;
  if (_.includes(path, "module-usage/planning")) {
    return PLANNING_USAGE;
  }
  return null;
};

export const renderMoreItemsComponent = columnData => {
  if (_.isEmpty(columnData)) return "-";
  return _.isArray(columnData)
    ? getMoreItemsWithToolTipComponent({ items: columnData })
    : columnData;
};

export const renderCellValue = ({
  columnData,
  appendPercentToValue = false,
}) => {
  if (_.isNumber(columnData)) return columnData;
  if (
    _.includes(
      ["null", "Invalid date", "1970-01-01", "1 January, 1970"], // 1 January, 1970 is hack to temporarily fix date issue
      columnData
    )
  )
    return "-";
  if (appendPercentToValue)
    return !_.isEmpty(columnData)
      ? appendPercent({ variable: columnData })
      : "-";

  return !_.isEmpty(columnData)
    ? _.isArray(columnData)
      ? _.join(columnData, ", ")
      : columnData
    : "-";
};

const getGradingPeriodFromAssociatedEntities = ({ associatedEntities }) => {
  const academicTermEntity = _.find(associatedEntities, entity => {
    const typename = _.get(entity, "__typename");
    return _.isEqual(typename, "AcademicTerm");
  });

  return academicTermEntity;
};

export const getColumnListWithDynamicColumnsV2 = ({
  t,
  columnList,
  dynamicColumnsMetaData,
  tableName,
  currentCurriculumId,
  selectedGradingPeriodIds,
}) => {
  const { type: currentCurriculumType } = getCurriculumProgramDetails(
    currentCurriculumId
  );
  const valueTypes = DYNAMIC_COLUMNS_VALUE_TYPES[currentCurriculumType];
  const insertPosition = DYNAMIC_COLUMNS_INSERT_INDEX_VALUE[tableName];
  const prependGradingPeriodToSubText = !_.isEqual(
    _.size(selectedGradingPeriodIds),
    1
  );

  const sortedMetaData = _.compact(
    _.map(selectedGradingPeriodIds, gradingPeriodId => {
      const metaData = _.find(dynamicColumnsMetaData, columnMetaData => {
        const associatedEntities = _.get(
          columnMetaData,
          "node.associatedEntities"
        );
        const associatedGradingPeriod = getGradingPeriodFromAssociatedEntities({
          associatedEntities,
        });
        const associatedGradingPeriodId = _.get(associatedGradingPeriod, "id");
        return _.isEqual(associatedGradingPeriodId, gradingPeriodId);
      });

      return metaData;
    })
  );

  const dynamicColumns = [];
  _.forEach(
    sortedMetaData,
    columnMetaData => {
      const categories = _.get(columnMetaData, "node.categories");

      const associatedEntities = _.get(
        columnMetaData,
        "node.associatedEntities"
      );
      const associatedGradingPeriod = getGradingPeriodFromAssociatedEntities({
        associatedEntities,
      });
      const associatedGradingPeriodLabel = _.get(
        associatedGradingPeriod,
        "label"
      );
      const associatedGradingPeriodId = _.get(associatedGradingPeriod, "id");

      _.forEach(categories, category => {
        const categoryLabel = _.get(category, "label");
        const categoryId = _.get(category, "id");
        const gradeBoundary = _.get(category, "gradeBoundary");

        _.forEach(valueTypes, valueType => {
          if (_.isEqual(valueType, "IB_DP_GRADE") && _.isEmpty(gradeBoundary))
            return;

          const dynamicColumnTypes = _.get(
            DYNAMIC_COLUMNS_VALUE_TYPES_VARIABLES,
            valueType,
            {}
          );

          const { configurationId, configurationLabel } = dynamicColumnTypes;
          let { subText } = dynamicColumnTypes;
          let scoreDisplayType;

          // Score can be of two sub types - Percentage or weights
          if (_.isEqual(valueType, "SCORE")) {
            const categoryTotalDisplayType = getCategoryTotalDisplayType({
              curriculumProgramId: currentCurriculumId,
            });

            if (_.isEqual(categoryTotalDisplayType, "WEIGHT")) {
              scoreDisplayType = "WEIGHT";
              subText = _.get(subText, "WEIGHT");
            } else {
              scoreDisplayType = "PERCENTAGE";
              subText = _.get(subText, "PERCENTAGE");
            }
          }

          const commonId = `${categoryId}_${valueType}_${associatedGradingPeriodId}`;
          const parentId = `${commonId}_parent`;
          const localizedSubText = getLocalisedText({ t, value: subText });

          const commonStyles = {
            height: "44px",
            fontSize: "14px",
          };
          const childStyles = {
            ...commonStyles,
            minWidth: DEFAULT_TABLE_COLUMN_WIDTH,
          };

          /*
          Reason why we added configurationId is we check children array using _.some
          whenever the object is a parent object.
          So whenever this configuration is configured the parent will be considered as
          configured
          */
          const childrenIds = [commonId, configurationId];
          const parentColumnData = getParentColumnData({
            id: parentId,
            label: categoryLabel,
            childrenIds,
            subText: localizedSubText,
            alignment: "right",
            currentCurriculumType,
            style: commonStyles,
          });

          dynamicColumns.push(parentColumnData);

          const childColumnData = getTableColumnData({
            id: commonId,
            parent: parentId,
            categoryId,
            valueType,
            gradingPeriodId: associatedGradingPeriodId,
            scoreDisplayType,
            currentCurriculumType,
            label: associatedGradingPeriodLabel,
            configurationId,
            configurationLabel,
            isGradingPeriodColumn: true,
            style: childStyles,
            columnHeight: "36px",
          });
          dynamicColumns.push(childColumnData);
        });
      });
    },
    []
  );

  const beforeColumns = _.slice(columnList, 0, insertPosition);
  const afterColumns = _.slice(columnList, insertPosition, _.size(columnList));

  return [...beforeColumns, ...dynamicColumns, ...afterColumns];
};

export const getColumnListWithDynamicColumns = ({
  columnList,
  dynamicColumnsMetaData,
  tableName,
  currentCurriculumId,
}) => {
  const { type: currentCurriculumType } = getCurriculumProgramDetails(
    currentCurriculumId
  );
  const valueTypes = DYNAMIC_COLUMNS_VALUE_TYPES[currentCurriculumType];
  const insertPosition = DYNAMIC_COLUMNS_INSERT_INDEX_VALUE[tableName];

  const dynamicColumns = [];
  _.forEach(
    dynamicColumnsMetaData,
    columnMetaData => {
      _.forEach(valueTypes, valueType => {
        const categoryLabel = _.get(columnMetaData, "label");
        const categoryId = _.get(columnMetaData, "id");
        const gradeBoundary = _.get(columnMetaData, "gradeBoundary");

        if (_.isEqual(valueType, "IB_DP_GRADE") && _.isEmpty(gradeBoundary))
          return;

        const dynamicColumnTypes = _.get(
          DYNAMIC_COLUMNS_VALUE_TYPES_VARIABLES,
          valueType,
          {}
        );

        const { configurationId, configurationLabel } = dynamicColumnTypes;
        let { subText } = dynamicColumnTypes;
        let scoreDisplayType;

        // Score can be of two sub types - Percentage or weights
        if (_.isEqual(valueType, "SCORE")) {
          const categoryTotalDisplayType = getCategoryTotalDisplayType({
            curriculumProgramId: currentCurriculumId,
          });

          if (_.isEqual(categoryTotalDisplayType, "WEIGHT")) {
            scoreDisplayType = "WEIGHT";
            subText = _.get(subText, "WEIGHT");
          } else {
            scoreDisplayType = "PERCENTAGE";
            subText = _.get(subText, "PERCENTAGE");
          }
        }

        const commonStyles = {
          width: DEFAULT_TABLE_COLUMN_WIDTH,
          height: "44px",
          fontSize: "14px",
        };

        const columnData = getTableColumnData({
          id: `${categoryId}_${valueType}`,
          categoryId,
          valueType,
          scoreDisplayType,
          currentCurriculumType,
          label: categoryLabel,
          subText,
          configurationId,
          configurationLabel,
          style: commonStyles,
        });
        dynamicColumns.push(columnData);
      });
    },
    []
  );

  const beforeColumns = _.slice(columnList, 0, insertPosition);
  const afterColumns = _.slice(columnList, insertPosition, _.size(columnList));

  return [...beforeColumns, ...dynamicColumns, ...afterColumns];
};

const getParentColumnData = ({
  id,
  label,
  subText,
  alignment,
  childrenIds,
  style,
  currentCurriculumType,
}) => {
  return {
    id,
    isParent: true,
    children: childrenIds,
    level: 0,
    label,
    subText,
    style,
    alignment,
    curriculumPrograms: [currentCurriculumType],
    isDynamicColumn: true,
    isSubTextLocalized: true,
  };
};

export const getTableColumnData = ({
  id,
  parent = undefined,
  categoryId,
  valueType,
  gradingPeriodId = null,
  scoreDisplayType,
  currentCurriculumType,
  label,
  subText,
  configurationId,
  configurationLabel,
  isSubTextLocalized = false,
  isGradingPeriodColumn = false,
  style,
  columnHeight = undefined,
}) => {
  return {
    id,
    parent,
    level: parent ? 1 : undefined,
    categoryId,
    valueType,
    label,
    isDynamicColumn: true,
    configurationId,
    configurationLabel,
    curriculumPrograms: [currentCurriculumType],
    subText,
    isSubTextLocalized,
    group: "toddleAnalytics:metrics",
    type: "TEXTCOLUMN",
    alignment: "right",
    scoreDisplayType,
    appendPercentToCell:
      _.isEqual("SCORE", valueType) &&
      _.isEqual("PERCENTAGE", scoreDisplayType),
    style,
    fieldUID: gradingPeriodId
      ? `CATEGORY-${categoryId}-${valueType}-${gradingPeriodId}`
      : `CATEGORY-${categoryId}-${valueType}`,
    sortType: "toggle",
    colorCoding: true,
    columnHeight,
    isGradingPeriodColumn,
  };
};

const getBucketIndex = ({ value, low, high, numBuckets }) => {
  if (_.isEqual(value, high)) return numBuckets - 1;
  const intervalSize = (high - low) / numBuckets;
  const bucketIndex = _.floor((value - low) / intervalSize);
  return bucketIndex;
};

const scaleScore = ({ absoluteScore, maxScore, targetMaxScore = 100 }) => {
  const absoluteScoreNum = _.toNumber(absoluteScore);
  if (_.isNaN(absoluteScoreNum)) return absoluteScore;
  return (absoluteScore / maxScore) * targetMaxScore;
};

export const getTableDataWithColorCodes = ({
  tableData,
  colorCodingFields,
  customRange,
  considerNormalisedScoreForScore = false,
  colorCodingRangeIdForLastRowInIbGrade = null,
}) => {
  const modifiedColorCodingRanges = isPerformanceThresholdEnabled()
    ? getModifiedColorCodingRanges()
    : COLORS_VALUE_RANGE;

  return _.map(tableData, tableRow => {
    tableRow["cellStyles"] = { ..._.get(tableRow, "cellStyles", {}) };

    _.forEach(colorCodingFields, field => {
      let ranges = !_.isEmpty(modifiedColorCodingRanges[field])
        ? modifiedColorCodingRanges[field]
        : customRange[field];

      const isEndOfMultiRow = _.get(tableRow, "isEndOfMultiRow", false);
      if (
        !_.isNull(colorCodingRangeIdForLastRowInIbGrade) &&
        isEndOfMultiRow &&
        _.isEqual(field, "ibGrade")
      ) {
        ranges =
          modifiedColorCodingRanges[colorCodingRangeIdForLastRowInIbGrade];
      }
      if (_.isEmpty(ranges)) return; //will only happen if developer does any mistake

      const fieldValue = tableRow[field];
      let originalValue = fieldValue;

      if (_.get(fieldValue, "normaliseBasedOnMaxScore")) {
        const absoluteScorePropertyName =
          fieldValue["absoluteScorePropertyName"]; // Must be passed
        const maxScorePropertyName = fieldValue["maxScorePropertyName"]; // Must be passed
        const maxScore = fieldValue[maxScorePropertyName];
        const absoluteScore = fieldValue[absoluteScorePropertyName];
        const normalisedScore = scaleScore({ absoluteScore, maxScore });
        originalValue = normalisedScore;
      }

      if (
        _.includes(["score", "averageScore"], field) &&
        considerNormalisedScoreForScore
      ) {
        originalValue = tableRow["normalisedScore"];
      }

      if (
        _.includes(["", " ", "-"], originalValue) ||
        _.isNull(originalValue)
      ) {
        return;
      }

      const value = _.toNumber(originalValue);
      if ((_.isEqual(value), NaN)) return;

      _.forEach(ranges, (range = {}, index) => {
        const { high, low } = range;

        if (_.isEqual(index, 0)) {
          if (low <= value && high >= value) {
            const bucketLength = _.size(COLOR_BUCKETS[index]);
            const bucketIndex = getBucketIndex({
              value,
              low,
              high,
              numBuckets: bucketLength,
            });
            if (bucketIndex >= bucketLength) return; //this never happens but just safety check
            tableRow["cellStyles"][field] = {
              ..._.get(tableRow, ["cellStyles", field], {}),
              ...COLOR_BUCKETS[index][bucketIndex],
            };
          }
        } else {
          if (low < value && high >= value) {
            const bucketLength = _.size(COLOR_BUCKETS[index]);
            const bucketIndex = getBucketIndex({
              value,
              low,
              high,
              numBuckets: bucketLength,
            });
            if (bucketIndex >= bucketLength) return; //this never happens but just safety check
            tableRow["cellStyles"][field] = {
              ..._.get(tableRow, ["cellStyles", field], {}),
              ...COLOR_BUCKETS[index][bucketIndex],
            };
          }
        }
      });
    });
    return tableRow;
  });
};

export const getColorCodingFieldsIds = ({ columnList }) => {
  return _.map(
    _.filter(columnList, column => _.get(column, "colorCoding")),
    column => {
      const id = _.get(column, "id");
      return id;
    }
  );
};

export const getColorCodingMetaDataForDynamicColumns = ({
  categoriesMetaData,
  currentCurriculumId,
}) => {
  const { type: currentCurriculumType } = getCurriculumProgramDetails(
    currentCurriculumId
  );

  const valueTypes = DYNAMIC_COLUMNS_VALUE_TYPES[currentCurriculumType];
  const dynamicColumnIds = [];
  const customRange = {};

  _.forEach(categoriesMetaData, columnMetaData => {
    _.forEach(valueTypes, valueType => {
      const categoryId = _.get(columnMetaData, "id");
      const id = `${categoryId}_${valueType}`;
      dynamicColumnIds.push(id);

      const { colorCodingRanges } = _.get(
        DYNAMIC_COLUMNS_VALUE_TYPES_VARIABLES,
        valueType,
        {}
      );
      customRange[id] = colorCodingRanges;
    });
  });

  return customRange;
};

export const getColorCodingMetaDataForDynamicColumnsV2 = ({ columnList }) => {
  const customRange = {};

  const modifiedColorCodingRanges = isPerformanceThresholdEnabled()
    ? getModifiedColorCodingRanges()
    : COLORS_VALUE_RANGE;

  _.forEach(columnList, column => {
    const id = _.get(column, "id");
    const colorCoding = _.get(column, "colorCoding");
    const isDynamicColumn = _.get(column, "isDynamicColumn");
    if (!colorCoding) return;

    const splittedId = _.split(id, "_");
    if (isDynamicColumn) {
      const valueType = _.get(splittedId, "1");
      const { colorCodingRanges } = _.get(
        DYNAMIC_COLUMNS_VALUE_TYPES_VARIABLES,
        valueType,
        {}
      );
      customRange[id] = colorCodingRanges;
    } else {
      const primaryId = _.first(splittedId);
      customRange[id] = modifiedColorCodingRanges[primaryId];
    }
  });

  return customRange;
};

export const applyBoldStyle = variable => {
  return { value: variable, cellStyles: { fontWeight: 600 } };
};

export const getOrganizationGlobalConstantsQuery = orgId => {
  return {
    organizationId: orgId,
    filter: {
      names: ["TIMEZONE"],
    },
  };
};

export const getOrganizationGlobalConstantsQueryVariablesMemoized = _.memoize(
  params => getOrganizationGlobalConstantsQuery(params),
  params => JSON.stringify(params)
);

export const getDynamicColumnIdsToAppendPercent = columnList =>
  _.map(
    _.filter(columnList, column => _.get(column, "appendPercentToCell")),
    column => _.get(column, "id")
  );

export const addLevelSubTextToColumns = ({ t, columnList, levelId }) => {
  const subText = getLevelLabel({ levelId });

  return _.map(columnList, column => {
    const addLevelInLabel = _.get(column, "addLevelInLabel", false);
    if (addLevelInLabel)
      column["label"] = !_.isEqual(levelId, "all")
        ? `${column["label"]} (${t(LEVEL_LOCALES[subText])})`
        : column["label"];
    return column;
  });
};

export const getCategoryTotalDisplayType = ({ curriculumProgramId }) => {
  return getSettingValue({
    name: "CategoryFinalScoreDisplayType",
    currentCurriculumProgramId: curriculumProgramId,
  });
};

export const getCategoryMaxWeight = ({ category, levelId }) => {
  const categoryValues = _.get(category, "values", []);
  if (levelId) {
    const value = _.find(categoryValues, ({ genericTag }) =>
      _.isEqual(_.get(genericTag, "id"), levelId)
    );
    return _.get(value, "value");
  }
  return _.get(categoryValues, "0.value");
};

export const getStudentViewByOptions = ({ t }) => {
  return [
    {
      value: "overall",
      label: t("toddleAnalytics:overall_performance"),
    },
    {
      value: "subject",
      label: t("toddleAnalytics:subject_wise"),
    },
  ];
};

export const getAvatarTableCellProps = ({
  columnData,
  columnName,
  useClassNameAcronym = false,
  showAvatar = false,
  ...rest
}) => {
  return {
    id: _.get(columnData, "id"),
    useClassNameAcronym,
    name: _.get(columnData, columnName),
    profileUrl: _.get(columnData, "profileUrl", null),
    levels: _.get(columnData, "levelLabels", []),
    showAvatar,
    ...rest,
  };
};

export const getIsSchoolAdminByRole = () => {
  const state = store?.getState();
  const userRole = _.get(state, "login.userInfo.role");
  return userRole === ADMIN;
};

export const isOverallScoreEnabled = ({ curriculumProgramId }) => {
  return getSettingValue({
    name: "OverallScoreForGradingPeriod",
    currentCurriculumProgramId: curriculumProgramId,
  });
};

export const isToddleAnalyticsEnabled = () => {
  if (isRnpConsumptionEnabled()) {
    return ACLStorage.getModuleSettingsValue(ENABLE_TODDLE_ANALYTICS);
  }

  // Toddle analytics should be accessible to all school admins and non-admin users who have permission to access it
  if (
    !getIsSchoolAdminByRole() &&
    !ACLStore.can(TODDLE_ANALYTICS_USER_BASED_ACCESS_PERM)
  ) {
    return false;
  }

  const state = store?.getState();
  const selectedCurriculumProgramId = getSelectedCurriculumId(state);
  const { type } = getCurriculumProgramDetails(selectedCurriculumProgramId);

  const isEngagementAnalyticsEnabled = ACLStorage.getPermissionValue(
    ENABLE_ENGAGEMENT_ANALYTICS
  );
  const isBehaviourAnalyticsEnabled = ACLStorage.getPermissionValue(
    ENABLE_BEHAVIOUR_ANALYTICS
  );
  const isModuleUsageAnalyticsEnabled = ACLStorage.getPermissionValue(
    ENABLE_MODULE_USAGE_ANALYTICS
  );
  const isAttendanceAnalyticsEnabled = ACLStorage.getPermissionValue(
    ENABLE_ATTENDANCE_ANALYTICS
  );

  // PYP doesn't have performance analytics
  if (_.isEqual(type, CURRICULUM_TYPE_PYP)) {
    return (
      isEngagementAnalyticsEnabled ||
      isBehaviourAnalyticsEnabled ||
      isModuleUsageAnalyticsEnabled ||
      isAttendanceAnalyticsEnabled
    );
  }

  // UBD performance analytics is accessible only if overall score is enabled
  if (_.isEqual(type, CURRICULUM_TYPE_UBD)) {
    const isPerformanceAnalyticsEnabled =
      ACLStorage.getPermissionValue(ENABLE_PERFORMANCE_ANALYTICS) &&
      isOverallScoreEnabled({
        curriculumProgramId: selectedCurriculumProgramId,
      });

    return (
      isEngagementAnalyticsEnabled ||
      isPerformanceAnalyticsEnabled ||
      isBehaviourAnalyticsEnabled ||
      isModuleUsageAnalyticsEnabled ||
      isAttendanceAnalyticsEnabled
    );
  }

  return checkIfAnyPermIsEnabled(TODDLE_ANALYTICS_PERMS);
};

export const getIdAfterSplit = id => _.get(_.split(id, "_"), "0");

export const getOverviewCardLoadingStates = ({
  isLoading,
  isOverviewMetricsDataInCache,
}) => {
  return {
    showLoader: isLoading && !isOverviewMetricsDataInCache,
    showErrorState: !isLoading && !isOverviewMetricsDataInCache,
  };
};

export const showNA = value => _.includes([NA, "null", null, undefined], value);

export const scrollBy = ({
  element,
  behavior = "smooth",
  delay = 0,
  left,
  top,
}) => {
  if (!element) return;

  _.delay(() => {
    element.scrollBy({ top, left, behavior });
  }, delay);
};

const getUserAcademicYears = () => {
  const userInfo = getUserInfo();
  const userId = userInfo.id;
  const academicYears = _.get(
    getGradingPeriodsFromCache({ id: userId }),
    "academicYears",
    []
  );

  return academicYears;
};

export const getLastNAcademicYearOptions = _.memoize(
  ({ n, includeCurrentAY = false }) => {
    const academicYears = getUserAcademicYears();
    let filteredAcademicYears = _.filter(
      academicYears,
      ({ isUpcomingAcademicYear }) => !isUpcomingAcademicYear
    );

    if (!includeCurrentAY) {
      filteredAcademicYears = _.filter(
        academicYears,
        ({ isCurrentAcademicYear }) => !isCurrentAcademicYear
      );
    }

    const sortedYears = _.orderBy(filteredAcademicYears, ["endDate"], ["desc"]);
    const topNYears = _.take(sortedYears, n);

    const academicYearOptions = _.reduce(
      topNYears,
      (options, { id, startDate, endDate }) => {
        const year = getYearDurationFormat({ startDate, endDate });
        options.push({ key: id, value: id, label: year });
        return options;
      },
      []
    );

    return academicYearOptions;
  },
  ({ n, includeCurrentAY = false }) => JSON.stringify({ n, includeCurrentAY })
);

export const getIsAttendanceEnabledFor2_0 = () => {
  return ACLStore.can(ENABLE_ATTENDANCE_2_0);
};

export const getAnalyticsEntityQueryVariables = ({
  entityType,
  moduleType,
  curriculumProgramId,
}) => {
  const { org_id: orgId } = getUserInfo();
  const {
    selectedAcademicYearId,
    selectedCurriculumProgramIds,
  } = extractIdsFromUrlSearchParams();
  const {
    CATEGORY_WISE_CODE,
    PERIOD,
    ATTENDANCE_TIER,
    ATTENDANCE_CATEGORY,
  } = ANALYTICS_ENTITY_TYPE_ENUMS;

  const shouldIncludeAcademicYearId =
    _.includes(
      [CATEGORY_WISE_CODE, PERIOD, ATTENDANCE_TIER, ATTENDANCE_CATEGORY],
      entityType
    ) && !_.isNil(selectedAcademicYearId);

  const shouldIncludeCurriculumProgramIds =
    curriculumProgramId ||
    (!_.isEmpty(selectedCurriculumProgramIds) &&
      entityType === CATEGORY_WISE_CODE &&
      getIsAttendanceEnabledFor2_0());

  return {
    id: orgId,
    analyticsEntityFilterInput: {
      entityType,
      ...(shouldIncludeAcademicYearId
        ? { academicYearId: selectedAcademicYearId }
        : {}),
      ...(shouldIncludeCurriculumProgramIds
        ? {
            curriculumProgramIds: curriculumProgramId
              ? [curriculumProgramId]
              : selectedCurriculumProgramIds,
          }
        : {}),
      ...(moduleType ? { moduleType } : {}),
    },
  };
};

const _getAnalyticsEntityColors = ({ entityType }) => {
  const queryVariables = getAnalyticsEntityQueryVariables({
    entityType,
  });

  const queryData = getAnalyticsEntityFromCache(queryVariables);

  const categoriesData = _.get(
    queryData,
    "analytics.analyticsEntityData.edges",
    []
  );

  return _.reduce(
    categoriesData,
    (result, data) => {
      const categoryId = _.get(data, "entityId");
      const color = _.get(data, "metaData.color");
      result[categoryId] = color;
      return result;
    },
    {}
  );
};

export const getAnalyticsEntityColors = _.memoize(
  _getAnalyticsEntityColors,
  ({ entityType }) => JSON.stringify(entityType)
);

export const getAnalyticsEntityData = ({ entityType }) => {
  const gradeEntityQueryVariables = getAnalyticsEntityQueryVariables({
    entityType,
  });
  return getAnalyticsEntityFromCache(gradeEntityQueryVariables);
};

const _getEntityIdNameMap = ({ entityType }) => {
  const entityData = getAnalyticsEntityData({ entityType });
  const categoriesData = _.get(
    entityData,
    "analytics.analyticsEntityData.edges",
    []
  );

  return _.reduce(
    categoriesData,
    (result, data) => {
      const { entityId, name } = data || {};
      result[entityId] = name;
      return result;
    },
    {}
  );
};

export const getEntityIdNameMap = _.memoize(
  _getEntityIdNameMap,
  ({ entityType }) => JSON.stringify(entityType)
);

export const getEntityIdsByCurriculum = ({
  entityType,
  curriculumProgramIds,
}) => {
  const entityCacheData = getAnalyticsEntityData({ entityType });
  const entityData = _.get(
    entityCacheData,
    "analytics.analyticsEntityData.edges"
  );

  const result = _.reduce(
    entityData,
    (result, data) => {
      const { entityId, curriculumProgramIds: entityCurriculumProgramIds } =
        data || {};

      const hasOverlap = _.some(entityCurriculumProgramIds, id =>
        _.includes(curriculumProgramIds, id)
      );

      if (hasOverlap) {
        result.push(entityId);
      }

      return result;
    },
    []
  );

  return _.uniq(result);
};

export const getBehaviourModuleNomenclature = () => {
  const userInfo = getUserInfo();
  const orgId = _.get(userInfo, "org_id");

  const queryData = getBehaviourModuleNomenclatureFromCache({ id: orgId });

  const globalConstants = _.get(queryData, "globalConstants", []);

  const NOMENCLATURE_TYPE_MAP = {
    BEHAVIOUR_MODULE_NOMENCLATURE_ACTION:
      BEHAVIOUR_NOMENCLATURE_CONSTANTS.ACTION,
    BEHAVIOUR_MODULE_NOMENCLATURE_LEVEL: BEHAVIOUR_NOMENCLATURE_CONSTANTS.LEVEL,
    BEHAVIOUR_MODULE_NOMENCLATURE_INCIDENT:
      BEHAVIOUR_NOMENCLATURE_CONSTANTS.INCIDENT,
    BEHAVIOUR_MODULE_NOMENCLATURE_MODULE:
      BEHAVIOUR_NOMENCLATURE_CONSTANTS.MODULE,
  };

  const nomenclatureConstants = {
    [BEHAVIOUR_NOMENCLATURE_CONSTANTS.INCIDENT]: "Incident",
    [BEHAVIOUR_NOMENCLATURE_CONSTANTS.ACTION]: "Action",
    [BEHAVIOUR_NOMENCLATURE_CONSTANTS.LEVEL]: "Level",
    [BEHAVIOUR_NOMENCLATURE_CONSTANTS.MODULE]: "Behaviour",
  };

  _.forEach(globalConstants, ({ name, value }) => {
    const nomenclatureKey = NOMENCLATURE_TYPE_MAP[name];
    if (
      _.isEqual(name, "BEHAVIOUR_MODULE_NOMENCLATURE_MODULE") &&
      _.includes(["Behaviour management", "Conduct management"], value)
    ) {
      nomenclatureConstants[nomenclatureKey] = _.get(_.split(value, " "), "0");
    } else {
      nomenclatureConstants[nomenclatureKey] = value;
    }
  });

  return nomenclatureConstants;
};

export const getNomenclatureLocaleParams = ({
  type,
  convertToLowerCase = true,
}) => {
  const nomenclatureConstants = getBehaviourModuleNomenclature();
  const localeParam = BEHAVIOUR_NOMENCLATURE_LOCALE_PARAM_MAP[type];
  let nomenclatureValue = _.get(nomenclatureConstants, type);
  if (nomenclatureValue && convertToLowerCase) {
    nomenclatureValue = _.toLower(nomenclatureValue);
  }
  return { [localeParam]: nomenclatureValue };
};

const getGradingPeriodDetails = gradingPeriodId => {
  const userInfo = getUserInfo();
  const userId = userInfo.id;
  const academicYears = _.get(
    getGradingPeriodsFromCache({ id: userId }),
    "academicYears",
    []
  );

  const gradingPeriodDetails = _.chain(academicYears)
    .flatMap("terms") // Flatten all terms into a single array
    .find({ id: gradingPeriodId }) // Find the term by id
    .value();

  return gradingPeriodDetails || {};
};

const mergeTitlesForSheetName = ({
  entityTitle,
  dashboardTitle,
  chartTitle,
  filterText,
  gradingPeriodText,
  durationText,
  programmesText,
}) => {
  return _.join(
    _.compact(
      [
        entityTitle,
        dashboardTitle,
        chartTitle,
        filterText,
        gradingPeriodText,
        durationText,
        programmesText,
      ].map(item => item?.replace(/\s+/g, "").trim()) // Trim and replace extra spaces
    ),
    "_"
  );
};

const getDashboardTitle = ({ tableName }) => {
  return DASHBOARD_TITLE_MAP[tableName];
};

const getChartTitle = ({ moduleName, tableName }) => {
  if (moduleName == ENGAGEMENT_ANALYTICS) return "DetailedReport";
  return CHART_TITLE_MAP[tableName];
};

const getDurationText = () => {
  const {
    dateViewType,
    rangeStartDate,
    rangeEndDate,
  } = extractIdsFromUrlSearchParams();
  const { rangeStart, rangeEnd } = getDateRange({
    dateViewType,
    rangeStartDate,
    rangeEndDate,
  });
  const dateFormat = "DDMMMYY";
  if (!rangeStart || !rangeEnd) return "";
  const formattedStartDate = moment.parseZone(rangeStart).format(dateFormat);
  const formattedEndDate = moment.parseZone(rangeEnd).format(dateFormat);
  return `${formattedStartDate}-${formattedEndDate}`;
};

const getProgrammesText = ({ moduleName }) => {
  const {
    selectedCurriculumId,
    selectedCurriculumProgramIds,
  } = extractIdsFromUrlSearchParams();

  if (
    _.includes(
      [ENGAGEMENT_ANALYTICS, MODULE_USAGE_ANALYTICS, ATTENDANCE_ANALYTICS],
      moduleName
    )
  ) {
    let text = "";
    _.forEach(selectedCurriculumProgramIds, (id, index) => {
      const { acronym } = getCurriculumProgramDetails(id);
      if (index == 0) text = acronym;
      else text = `${text},${acronym}`;
    });
    return text;
  } else {
    const { acronym } = getCurriculumProgramDetails(selectedCurriculumId);
    return acronym;
  }
};

const getGradingPeriodTextForExports = () => {
  const { gradingPeriodId, gradingPeriodIds } = getGradingPeriodProps();
  const isRevampPermEnabled = checkIsRevampPermEnabled();
  if (!isRevampPermEnabled) {
    const { label } = getGradingPeriodDetails(gradingPeriodId);
    return label;
  }

  return _.join(
    _.map(gradingPeriodIds, id => {
      const { label } = getGradingPeriodDetails(id);
      return label;
    }),
    "_"
  );
};

const getFilterText = ({ tableName }) => {
  return FILTER_MAP[tableName];
};

export const generateSheetName = ({ moduleName, tableName, entityTitle }) => {
  // Dashboard title
  const dashboardTitle = getDashboardTitle({ tableName });

  // Chart title
  const chartTitle = getChartTitle({ moduleName, tableName });

  //Filter title
  let filterText = "";
  if (_.isEqual(moduleName, MODULE_USAGE_ANALYTICS)) {
    filterText = getFilterText({ tableName });
  }

  // Grading period
  let gradingPeriodText = "";
  if (moduleName == PERFORMANCE_ANALYTICS) {
    // Please consider changing the below function if another module is added for
    // dispalying grading period label in future (for any module)
    gradingPeriodText = getGradingPeriodTextForExports();
  }

  // Duration
  let durationText = "";
  if (
    _.includes(
      [
        ENGAGEMENT_ANALYTICS,
        BEHAVIOUR_ANALYTICS,
        MODULE_USAGE_ANALYTICS,
        ATTENDANCE_ANALYTICS,
      ],
      moduleName
    )
  ) {
    durationText = getDurationText();
  }

  // Programmes
  const programmesText = getProgrammesText({ moduleName });

  return mergeTitlesForSheetName({
    entityTitle,
    dashboardTitle,
    chartTitle,
    filterText,
    gradingPeriodText,
    durationText,
    programmesText,
  });
};

export const getSheetNameAfterNomenclature = ({ sheetName }) => {
  let resultantSheetName = sheetName;

  _.forEach(_.keys(BEHAVIOUR_NOMENCLATURE_CONSTANTS), type => {
    const lowerCaseSheetName = _.toLower(resultantSheetName);
    const value = BEHAVIOUR_NOMENCLATURE_CONSTANTS[type];
    const lowerCaseValue = _.toLower(value);

    //Check whether sheet name contains nomenclature key and replace it with applied nomenclature
    if (_.includes(lowerCaseSheetName, lowerCaseValue)) {
      const nomenclatureParams = getNomenclatureLocaleParams({
        type,
        convertToLowerCase: false,
      });
      resultantSheetName = _.replace(
        resultantSheetName,
        _.capitalize(value),
        _.get(nomenclatureParams, lowerCaseValue)
      );
    }
  });
  return resultantSheetName;
};

export const getLastSyncTimeQueryVariables = () => {
  const userInfo = getUserInfo();
  const orgId = _.get(userInfo, "org_id");

  const path = location.pathname;
  const moduleName = getModuleName(path);
  return {
    id: orgId,
    lastSyncTimeFilterInput: {
      moduleType: moduleName,
    },
  };
};

export const getLastSyncTime = ({ t }) => {
  const queryVariables = getLastSyncTimeQueryVariables();
  const queryData = getLastSyncTimeFromCache(queryVariables);
  const lastSyncDetails = _.get(queryData, "analytics.lastSyncTime", []);
  if (_.isEmpty(lastSyncDetails)) return null;

  let lastSyncTime = _.find(lastSyncDetails, {
    entityType: FILTER_ORGANIZATION,
  });
  if (_.isEmpty(lastSyncTime)) {
    const { selectedCurriculumId } = extractIdsFromUrlSearchParams();
    lastSyncTime = _.find(
      lastSyncDetails,
      ({ entityType, entityId }) =>
        _.isEqual(entityType, FILTER_CURRICULUM_PROGRAM) &&
        _.isEqual(entityId, selectedCurriculumId)
    );
  }

  const lastSyncTimeStamp = _.get(lastSyncTime, "timestamp");

  if (!lastSyncTimeStamp) return null;

  const lastSyncedDateWithTime = formatDate({
    date: moment.utc(lastSyncTimeStamp),
    format: DATE_TIME_CONSTANTS.Do_MMM_YYYY_h_mm_A,
  });

  return t("toddleAnalytics:last_updated_on_with_date_and_time", {
    date_and_time: lastSyncedDateWithTime,
  });
};

export const getTextWidth = tickFontSize => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  return text => {
    context.font = `500 ${tickFontSize}px AvenirNext, Avenir Next, Arial, sans-serif, system-ui`;
    const metrics = context.measureText(text);
    return metrics.width;
  };
};

export const getGradingPeriodLabel = ({ gradingPeriodId }) => {
  const { label } = getGradingPeriodDetails(gradingPeriodId);
  return label;
};

export const getGradingPeriodAbbreviation = ({ gradingPeriodId }) => {
  const { abbreviation } = getGradingPeriodDetails(gradingPeriodId);
  return abbreviation;
};

export const getSortOptions = ({ userType }) => {
  const sortOptions = NAME_SORT_OPTIONS;

  return getUserSortOptions({
    type: userType,
    sortOptions,
    keyToMap: "filterKey",
  });
};

export const getColumnIdsWithMetaData = ({ columnList }) => {
  const filteredList = _.filter(columnList, column => {
    return _.get(column, "doesIdContainsMetaData");
  });

  return _.map(filteredList, column => _.get(column, "id"));
};

export const checkIsRevampPermEnabled = () => {
  return ACLStore.can("FeatureFlag:PerformanceAnalyticsRevamp");
};

export const getGradingPeriodColors = () => {
  const {
    selectedAcademicYearId,
    selectedCurriculumId,
  } = extractIdsFromUrlSearchParams();

  const gradingPeriodOptions = getGradingPeriodsMemoized({
    curriculumId: selectedCurriculumId,
    academicYearId: selectedAcademicYearId,
  });

  return _.reduce(
    gradingPeriodOptions,
    (result, { key }, index) => {
      result[key] =
        PERFORMANCE_ANALYTICS_COLOR_PANEL[
          index % _.size(PERFORMANCE_ANALYTICS_COLOR_PANEL)
        ];
      return result;
    },
    {}
  );
};

// curriculumId is optional
const _getGradeLabelFromId = ({ gradeId, curriculumId }) => {
  const gradeOptions = getGradeOptions({ curriculumId });
  return _.find(gradeOptions, { key: gradeId })?.label;
};

export const getGradeLabelFromId = _.memoize(
  _getGradeLabelFromId,
  ({ gradeId, curriculumId }) => JSON.stringify({ gradeId, curriculumId })
);

export const getGradingPeriodColumnsColorRanges = ({
  columnIds,
  curriculumProgramId,
}) => {
  const customRange = {};

  const modifiedColorCodingRanges = isPerformanceThresholdEnabled()
    ? getModifiedColorCodingRanges({ curriculumProgramId })
    : COLORS_VALUE_RANGE;

  _.forEach(columnIds, columnId => {
    const splittedId = _.split(columnId, "_");
    const primaryId = _.first(splittedId);

    customRange[columnId] = modifiedColorCodingRanges[primaryId];
  });

  return customRange;
};

// orders the lineOptions based on whether the dataKey has all zero values
// so that the zero value lines are hidden by the non-zero value lines

export const isPerformanceThresholdEnabled = () => {
  return ACLStore.can("FeatureFlag:PerformanceThresholds");
};

export const getThresholdValue = ({ perm }) => {
  const { selectedCurriculumId } = extractIdsFromUrlSearchParams();

  return getSettingValue({
    name: perm,
    currentCurriculumProgramId: selectedCurriculumId,
  });
};

export const getModifiedColorCodingRanges = () => {
  const modifiedColorCodingRanges = {};

  _.forEach(_.keys(COLORS_VALUE_RANGE), (id, key) => {
    const thresholdSettingPerm = _.get(COLUMN_ID_THRESHOLD_PERM_MAPPING, id);
    const thresholdValue = getThresholdValue({
      perm: thresholdSettingPerm,
    });

    modifiedColorCodingRanges[id] = [
      {
        low: 0,
        high: thresholdValue,
      },
      //mid and high buckets not usefulCurrently but when in future
      // if required the strucutre has been setup
      {
        low: thresholdValue,
        high: thresholdValue,
      },
      {
        low: thresholdValue,
        high: 100,
      },
    ];
  });

  return modifiedColorCodingRanges;
};

export const getChartAriaTitle = ({
  t,
  by,
  heading,
  chartType = "AREA_CHART",
}) => {
  if (!heading || !chartType) return "";

  const suffix = by ? "_by" : "";
  const chartTypeMap = {
    AREA_CHART: `toddleAnalytics:area_chart_showing_with_description${suffix}`,
    LINE_CHART: `toddleAnalytics:line_chart_showing_with_description${suffix}`,
    BAR_CHART: `toddleAnalytics:bar_chart_showing_with_description${suffix}`,
    BOX_PLOT: `toddleAnalytics:box_plot_showing_with_description${suffix}`,
    RADAR_CHART: `toddleAnalytics:radar_chart_showing_with_description${suffix}`,
  };

  const translationKey = chartTypeMap[chartType];
  const result = t(translationKey, { description: _.toLower(heading), by });

  // Remove all "?" that are not at the end of the string
  return _.replace(result, /\?(?!$)/g, "");
};

export const fetchAnalyticsEntityQueryVariables = ({
  entityType,
  additionalFilters,
}) => {
  const { org_id: orgId } = getUserInfo();

  const {
    selectedCurriculumId,
    selectedCurriculumProgramIds,
    selectedAcademicYearId,
  } = extractIdsFromUrlSearchParams();

  const moduleName = getModuleName(location.pathname);
  const isAttendanceAnalytics = _.isEqual(moduleName, ATTENDANCE_ANALYTICS);

  return {
    id: orgId,
    analyticsEntityFilterInput: {
      entityType,
      curriculumProgramIds: _.compact(
        isAttendanceAnalytics
          ? selectedCurriculumProgramIds
          : [selectedCurriculumId]
      ),
      academicYearId: selectedAcademicYearId,
      moduleType: getModuleName(location.pathname),
      ...additionalFilters,
    },
  };
};

export const fetchAnalyticsEntitesByType = ({
  entityType,
  additionalFilters,
}) => {
  const queryVariables = fetchAnalyticsEntityQueryVariables({
    entityType,
    additionalFilters,
  });
  const queryData = getAnalyticsEntityFromCache(queryVariables);
  const analyticsEntityData = _.get(queryData, "analytics.analyticsEntityData");
  return _.get(analyticsEntityData, "edges", []);
};

export const getEntityOptions = ({ entityType, additionalFilters }) => {
  const entities = fetchAnalyticsEntitesByType({
    entityType,
    additionalFilters,
  });
  return _.map(entities, ({ entityId, name, curriculumProgramIds }) => {
    return {
      key: entityId,
      value: entityId,
      label: name,
      moreInfo: { curriculumProgramIds },
    };
  });
};

export const getCurriculumRecordingType = id => {
  const userInfo = getUserInfo();
  const orgId = userInfo.org_id;
  const { selectedAcademicYearId } = extractIdsFromUrlSearchParams();
  const globalConstants = getGlobalConstansData({
    orgId,
    academicYearId: selectedAcademicYearId,
  });

  const curriculumConstants = _.find(globalConstants, constant => {
    const name = _.get(constant, "name", "");
    return (
      _.isEqual(name, ATTENDANCE_GLOBAL_CONSTANTS.ATTENDANCE_RECORDING_TYPE) &&
      _.isEqual(_.get(constant, "curriculumProgram.id"), id)
    );
  });

  const recordingTypeValue = _.get(curriculumConstants, "value", "");
  if (
    _.includes(
      recordingTypeValue,
      ATTENDANCE_RECORDING_TYPE.EACH_PERIOD_WITH_ONCE_A_DAY
    )
  ) {
    return "EACH_PERIOD_WITH_ONCE_A_DAY";
  } else if (
    _.includes(recordingTypeValue, ATTENDANCE_RECORDING_TYPE.EACH_PERIOD)
  ) {
    return "EACH_PERIOD";
  } else if (
    _.includes(recordingTypeValue, ATTENDANCE_RECORDING_TYPE.ONCE_A_DAY)
  ) {
    return "ONCE_A_DAY";
  }
  return "EACH_PERIOD_WITH_ONCE_A_DAY"; // Default type
};

const getGlobalConstantsQueryFilter = ({
  currentAcademicYearId,
  curriculumProgramIds,
}) => {
  return {
    academicYearIds: [currentAcademicYearId],
    curriculumProgramIds,
    names: [
      ATTENDANCE_GLOBAL_CONSTANTS.ATTENDANCE_RECORDING_TYPE,
      ATTENDANCE_GLOBAL_CONSTANTS.OPERATIONAL_DAYS,
    ],
  };
};

export const getMemoizedGlobalConstantsQueryFilter = _.memoize(
  getGlobalConstantsQueryFilter,
  ({ currentAcademicYearId, curriculumProgramIds }) =>
    JSON.stringify({ currentAcademicYearId, curriculumProgramIds })
);

export const getGlobalConstansData = ({ orgId, academicYearId }) => {
  const globalConstantsQueryFilter = getMemoizedGlobalConstantsQueryFilter({
    currentAcademicYearId: academicYearId,
  });

  const globalConstantsData = getOrganizationGlobalConstantsFromCache({
    organizationId: orgId,
    filters: globalConstantsQueryFilter,
  });

  const globalConstants = _.get(
    globalConstantsData,
    "node.globalConstants",
    []
  );

  return globalConstants;
};

export const getClassAttendanceAnalyticsCurriculumOptions = () => {
  const userInfo = getUserInfo();
  let curriculumOptions = getCurriculumOptions({
    userCurriculumPrograms: userInfo.userCurriculumPrograms,
    perm: MODULE_CONFIGURATION_MAP[ATTENDANCE_ANALYTICS],
  });
  curriculumOptions = _.filter(curriculumOptions, curriculum => {
    return _.includes(
      [
        ATTENDANCE_RECORDING_TYPE.EACH_PERIOD,
        ATTENDANCE_RECORDING_TYPE.EACH_PERIOD_WITH_ONCE_A_DAY,
      ],
      getCurriculumRecordingType(curriculum.key)
    );
  });
  return curriculumOptions;
};

export const checkIsTextOverflowing = refOrNode => {
  let node;

  if (refOrNode instanceof HTMLElement) {
    node = refOrNode;
  } else {
    node = refOrNode?.current;
  }

  if (!node) return false;

  const { clientWidth, clientHeight, scrollWidth, scrollHeight } = node;

  return scrollWidth > clientWidth || scrollHeight > clientHeight;
};

export const getOverviewCardV3Props = () => {
  const styles = {
    contentContainerStyles: {
      padding: "12px 16px",
    },
    boxParentContainerStyle: {
      gap: "4px",
    },
  };

  return {
    version: "3.0",
    boxVersion: "4.0",
    isRevampEnabled: true,
    helpIconTooltipStyles: styles.contentContainerStyles,
    customOverviewBoxHeadingClass: "text-heading-2",
    customOverviewBoxSubtitleClass: "text-body-l",
    boxParentContainerStyle: styles.boxParentContainerStyle,
  };
};

export const customOutsideClick = () => {
  const event = new MouseEvent("mousedown", {
    bubbles: true,
    cancelable: true,
    view: window,
  });

  document.body.dispatchEvent(event);
};

export const shouldShowConfigureColumnsDropdown = () => {
  return ACLStore.can("FeatureFlag:ConfigureColumnsV2");
};

export const checkIsShowingScroll = ({ container }) => {
  if (!container) return false;

  const { scrollHeight, clientHeight } = container || {};
  return scrollHeight > clientHeight;
};

export const getFullName = ({ firstName, middleName, lastName } = {}) =>
  _.compact([firstName, middleName, lastName]).join(" ");

export const getLoaderObjects = (count = 5) => {
  return Array.from({ length: count }, (_, index) => ({
    id: `loader-${index}`,
    showLoader: true,
  }));
};

export const getIsAIDebugModeEnabled = () => {
  return (
    ACLStore.can("IdentityAccess:EnableAIDebugMode") ||
    ACLStore.can("Common:EnableAIDebugMode")
  );
};

export const getIsAttendanceTierEnabled = () => {
  return ACLStore.can(ENABLE_ATTENDANCE_TIERS);
};

export const getIsAttendanceMarkingRateAndStatusVisible = () => {
  return ACLStore.can(ENABLE_MARKING_RATE_VISIBILITY);
};

export const getInsightDashboardQueryVariables = () => {
  return {
    id: getUserInfo()?.org_id,
    input: {
      paginationFilters: {
        first: 20,
      },
    },
  };
};

export const getManualSyncEligibilityVariables = modules => {
  const userInfo = getUserInfo();
  const orgId = userInfo.org_id;

  return {
    id: orgId,
    input: {
      modules,
    },
  };
};

export const getIsNewGradingSetupEnabled = () => {
  return getIsConfigurationEnabledForCPAY({
    configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
  });
};

export const getIsPerformance2p0Setup = () => {
  // add condition for new grading setup as well
  return getIsLearningCoursesEnabled() || getIsNewGradingSetupEnabled();
};

export const getIsPerformanceInsights2p0Enabled = () => {
  return (
    getIsPerformance2p0Setup() &&
    ACLStore.can("FeatureFlag:EnablePerformanceInsights2p0")
  );
};

// Returns either "performance-by-subject" or "performance-by-learning-course"
export const getBySubjectOrLearningCourseOption = () => {
  // If performance 2.0 is not enabled, we always show by subject
  if (!getIsPerformanceInsights2p0Enabled()) {
    return "performance-by-subject";
  }

  return getIsCourseBasedGradingEnabled()
    ? "performance-by-learning-course"
    : "performance-by-subject";
};

export const getOrganizationLearningCoursesQueryVariables = ({
  orgId,
  curriculumId,
  academicYearId,
}) => {
  return {
    organizationId: orgId,
    learningCourseFilters: {
      types: ["TEACHER_COURSE"],
      curriculumProgramIds: curriculumId ? [curriculumId] : [],
      academicYears: academicYearId ? [academicYearId] : [],
      fetchAllCourses: false,
    },
  };
};

export const getIsEnableAccountConfigurationTerminology = () => {
  return ACLStore.can(ENABLE_ACCOUNT_CONFIGURATION_TERMINOLOGY);
};

export const getIsExternalFileEditEnabled = () => {
  return ACLStore.can(ENABLE_EXTERNAL_FILE_EDIT);
};
